import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type MutableRefObject } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { ContactShadows, OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import {
  cannonballTransformOffset,
  cellTransformOffset,
  getDemolitionSnapshot,
  projectileToolAt,
  projectilePowerAt,
  setShardParticleCount,
  spinDynamicChunk,
  CANNONBALL_RADIUS,
  DEFAULT_WEAPON_POWER,
  MIN_WEAPON_POWER,
  MAX_WEAPON_POWER,
  type DemoTool,
  type DemolitionRuntime,
} from '../structures/demolition/demolitionPhysics';
import {
  getDemolitionPageSession,
  type DemolitionPageSession,
} from '../core/pageSessions/demolitionPageSession';
import {
  DESTRUCTION_PROFILES,
  PROFILE_IDS,
  type ProfileId,
} from '../services/destructionProfiles';
import {
  addSurfaceRestPositionAttribute,
  assignWallBoxAtlasUvs,
  cellToBufferGeometry,
  INTERIOR_TILE_METERS,
  type AtlasRegions,
  type CellGeometryOptions,
} from '../services/fracture/cellGeometry';
import { useViewportWidth } from '../hooks/useViewportWidth';
import ConcreteMaterialLab from '../components/ConcreteMaterialLab';
import type { ConcretePreset, ConcreteShowcaseId } from '../components/concreteMaterialLabData';
import type { FractureCell } from '../services/fracture/voronoiFracture';
import {
  bindScannedConcreteTextures,
  DEFAULT_CONCRETE_SETTINGS,
  ensureScannedConcreteTextures,
  type ConcreteMaterialSettings,
  type ConcreteQuality,
  type ConcreteSourceMode,
} from '../materials/authoring/ConcreteMaterial';
import { cloneProfileMaterial, createProfileMaterial, getSurface, updateProfileMaterial } from '../materials/MaterialRegistry';
import {
  bakeDemolitionProfile,
  isBakedProfile,
  type DemolitionBakeHandle,
} from '../materials/core/DemolitionBakedMaterials';
import type { BakedAtlasMaps } from '../materials/core/SurfaceBaker';
import {
  createDemolitionVisualState,
  resolveDemolitionEffectTier,
  type DemolitionEffectTier,
  type DemolitionVisualState,
} from '../effects/presentation/effectTier';
import { ImpactFx, type DemolitionFxController } from '../effects/fallback/ImpactFx';
import {
  DemolitionParticleLayer,
  overlayPresentationParticles,
  type DemolitionParticleController,
} from '../effects/particles';
import { dispatchDemolitionPresentation } from '../effects/presentation/demolitionPresentationDispatcher';
import { Phase1DamageLayer } from '../effects/presentation/Phase1DamageLayer';
import {
  PowderResidueLayer,
  type PowderResidueController,
} from '../effects/presentation/PowderResidueLayer';
import { FirstHitPrewarm } from '../effects/presentation/FirstHitPrewarmLayer';
import './Demolition.css';
import '../components/MobileLabChrome.css';

/** Touch tap: max duration (ms) and max movement (px) to count as fire, not orbit. */
const TAP_MAX_MS = 250;
const TAP_MAX_PX = 8;
const MOBILE_BREAKPOINT = 768;
const BAKE_DEBOUNCE_MS = 200;
const CAMERA_IDLE_POS2 = 1e-10;
const CAMERA_IDLE_ANGLE = 1e-5;

interface DemoBakeUiSnapshot {
  ready: boolean;
  busy: boolean;
  bakedActive: boolean;
  bakeMs: number;
  bakeSeq: number;
  bakeFailed: boolean;
}

let demoBakeBusyCount = 0;
let demoBakeUi: DemoBakeUiSnapshot = {
  ready: false,
  busy: false,
  bakedActive: false,
  bakeMs: 0,
  bakeSeq: 0,
  bakeFailed: false,
};
const demoBakeListeners = new Set<() => void>();

function demoBakeNotify(): void {
  for (const listener of demoBakeListeners) listener();
}

function demoBakeBegin(): void {
  demoBakeBusyCount += 1;
  demoBakeUi = { ...demoBakeUi, busy: true, bakeFailed: false };
  demoBakeNotify();
}

function demoBakeEnd(patch: Partial<DemoBakeUiSnapshot>): void {
  demoBakeBusyCount = Math.max(0, demoBakeBusyCount - 1);
  demoBakeUi = {
    ...demoBakeUi,
    ...patch,
    busy: demoBakeBusyCount > 0,
  };
  demoBakeNotify();
}

function useDemoBakeUi(): DemoBakeUiSnapshot {
  return useSyncExternalStore(
    (onChange) => {
      demoBakeListeners.add(onChange);
      return () => demoBakeListeners.delete(onChange);
    },
    () => demoBakeUi,
  );
}

function CameraDampingCutoff({
  controlsRef,
}: {
  controlsRef: MutableRefObject<OrbitControlsImpl | null>;
}) {
  const { camera } = useThree();
  const prevPos = useRef(new THREE.Vector3());
  const prevTarget = useRef(new THREE.Vector3());
  const prevQuat = useRef(new THREE.Quaternion());
  const primed = useRef(false);

  useFrame(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    if (!primed.current) {
      prevPos.current.copy(camera.position);
      prevTarget.current.copy(controls.target);
      prevQuat.current.copy(camera.quaternion);
      primed.current = true;
      return;
    }
    const posD2 = camera.position.distanceToSquared(prevPos.current);
    const tgtD2 = controls.target.distanceToSquared(prevTarget.current);
    const angle = camera.quaternion.angleTo(prevQuat.current);
    if (posD2 < CAMERA_IDLE_POS2 && tgtD2 < CAMERA_IDLE_POS2 && angle < CAMERA_IDLE_ANGLE) {
      camera.position.copy(prevPos.current);
      controls.target.copy(prevTarget.current);
      camera.quaternion.copy(prevQuat.current);
      camera.updateMatrixWorld();
    } else {
      prevPos.current.copy(camera.position);
      prevTarget.current.copy(controls.target);
      prevQuat.current.copy(camera.quaternion);
    }
  });

  return null;
}

/**
 * Marketing-capture mode, opted into with `?capture=1`.
 *
 * Only effect: turns on `preserveDrawingBuffer` for the WebGL context so
 * `canvas.captureStream()` can actually read frames. A GPU-composited canvas
 * without it hands the recorder a repeated still frame -- verified as 300+
 * "frames" with inter-frame motion ~0.001 while dynamicChunks and fps proved
 * the simulation was running normally. Costs a per-frame buffer copy, so it
 * stays off for every real visitor.
 */
const CAPTURE_MODE =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('capture') === '1';

type ConcreteLightingMode = 'dramatic' | 'neutral' | 'overcast' | 'wet';

interface ConcreteTourStep {
  showcase: ConcreteShowcaseId;
  preset: ConcretePreset;
  lighting: ConcreteLightingMode;
  quality?: ConcreteQuality;
  camera: [number, number, number, number, number, number, number?];
  action?: 'impact' | 'blast' | 'stress';
  waitMs?: number;
  autoOrbit?: boolean;
}

const DEMO_WEAPON_OPTIONS: Array<{
  id: DemoTool;
  label: string;
  shortLabel: string;
  icon: string;
  effect: string;
}> = [
  { id: 'cannon', label: 'Steel ball', shortLabel: 'Ball', icon: '●', effect: 'Focused impact' },
  { id: 'bomb', label: 'Sticky charge', shortLabel: 'Charge', icon: '◆', effect: 'Delayed blast' },
  { id: 'spike', label: 'Breaching spike', shortLabel: 'Spike', icon: '▲', effect: 'Shear-heavy puncture' },
  { id: 'rubble', label: 'Rubble chunk', shortLabel: 'Rubble', icon: '■', effect: 'Heavy blunt hit' },
  { id: 'concussion', label: 'Concussion canister', shortLabel: 'Blast', icon: '✦', effect: 'Small area blast' },
];

const CONCRETE_TOUR_STEPS: ConcreteTourStep[] = [
  { showcase: 'hero', preset: 'hero', lighting: 'dramatic', camera: [7.5, 3.8, 8.5, 0, 2.0, 0, 45] },
  { showcase: 'gallery', preset: 'aged', lighting: 'neutral', camera: [6.2, 3.0, 7.2, 0, 2.0, 0, 42] },
  { showcase: 'gallery', preset: 'wet', lighting: 'wet', camera: [6.2, 3.0, 7.2, 0, 2.0, 0, 42] },
  { showcase: 'gallery', preset: 'dusty', lighting: 'overcast', camera: [6.2, 3.0, 7.2, 0, 2.0, 0, 42] },
  { showcase: 'gallery', preset: 'aggregate', lighting: 'neutral', camera: [4.5, 2.5, 5.2, 0, 2.0, 0, 38] },
  { showcase: 'lighting', preset: 'hero', lighting: 'neutral', camera: [6.2, 3.0, 7.2, 0, 2.0, 0, 42] },
  { showcase: 'lighting', preset: 'hero', lighting: 'dramatic', camera: [6.2, 3.0, 7.2, 0, 2.0, 0, 42] },
  { showcase: 'scale', preset: 'hero', lighting: 'neutral', camera: [8.4, 3.9, 9.4, 0, 2.0, 0, 45] },
  { showcase: 'scale', preset: 'hero', lighting: 'neutral', camera: [3.8, 2.35, 4.3, 0, 2.1, 0, 34] },
  { showcase: 'mapping', preset: 'hero', lighting: 'dramatic', camera: [5.3, 2.8, 5.8, 0, 2.05, 0, 40], action: 'impact', waitMs: 550, autoOrbit: true },
  { showcase: 'impact', preset: 'aggregate', lighting: 'dramatic', camera: [4.6, 2.55, 5.1, 0, 2.0, 0, 38], action: 'impact', waitMs: 550 },
  { showcase: 'blast', preset: 'aggregate', lighting: 'dramatic', camera: [7.5, 3.8, 8.5, 0, 2.0, 0, 45], action: 'blast', waitMs: 550 },
  { showcase: 'settled', preset: 'aged', lighting: 'overcast', camera: [8.2, 2.5, 9.4, 0, 1.25, 0, 44], action: 'blast', waitMs: 3800 },
  { showcase: 'quality', preset: 'hero', lighting: 'neutral', quality: 'high', camera: [6.2, 3.0, 7.2, 0, 2.0, 0, 42] },
  { showcase: 'stress', preset: 'hero', lighting: 'dramatic', quality: 'low', camera: [7.5, 3.8, 8.5, 0, 1.9, 0, 45], action: 'stress', waitMs: 750 },
];

// textureScale is meters/tile for both sources. Hero/Aged/Wet/Dusty share the same
// scale so state layers alone drive visual difference; Aggregate uses the same basis
// with interiorAmount/aggMask controlling mineral coverage.
const CONCRETE_PRESET_PATCHES: Record<ConcretePreset, Partial<ConcreteMaterialSettings>> = {
  hero: {
    surfaceState: 'hero',
    textureScale: 0.72,
    roughness: 0.76,
    normalStrength: 0.42,
    macroVariation: 0.38,
    moisture: 0.06,
    interiorAmount: 0.2,
  },
  aged: {
    surfaceState: 'aged',
    textureScale: 0.72,
    roughness: 0.84,
    normalStrength: 0.48,
    macroVariation: 0.62,
    moisture: 0.02,
    interiorAmount: 0.28,
  },
  wet: {
    surfaceState: 'wet',
    textureScale: 0.72,
    roughness: 0.48,
    normalStrength: 0.34,
    macroVariation: 0.32,
    moisture: 0.86,
    interiorAmount: 0.15,
  },
  dusty: {
    surfaceState: 'dusty',
    textureScale: 0.72,
    roughness: 0.98,
    normalStrength: 0.36,
    macroVariation: 0.48,
    moisture: 0,
    interiorAmount: 0.32,
  },
  aggregate: {
    surfaceState: 'aggregate',
    textureScale: 0.72,
    roughness: 0.93,
    normalStrength: 0.62,
    macroVariation: 0.48,
    moisture: 0.05,
    interiorAmount: 0.92,
  },
};

// ---------------------------------------------------------------------------
// Window bridge
// ---------------------------------------------------------------------------

interface DemolitionState {
  loaded: boolean;
  material: string;
  cellCount: number;
  staticCells: number;
  dynamicChunks: number;
  removedChunks: number;
  awake: number;
  fractured: boolean;
  lastImpact: { x: number; y: number; z: number; speed: number } | null;
  collapsedUnsupported: number;
  fps: number;
  canvasReady: boolean;
  /** Max world Y among live dynamic chunk centers. */
  maxDynamicY: number;
  /** Chunks mid 240-cap fade-out (should be 0 during active collapse). */
  fadingChunks: number;
  /** Live gen-1 sub-fracture physics bodies. */
  subChunks: number;
  /** Live ballistic shard particles (micro-debris). */
  shardParticles: number;
  /** Volume range among live dynamic physics chunks. */
  minChunkVol: number;
  maxChunkVol: number;
  /** Glass impact-time partition wall-clock ms (0 for load-time materials). */
  lastPartitionMs?: number;
  /** First-fracture / subsequent-carve wall-clock ms. */
  lastCarveMs?: number;
  /** Active tool: cannon | bomb. */
  tool?: DemoTool;
  /** Live thrown projectiles, including balls currently bouncing through rubble. */
  projectileCount?: number;
  /** Current power for newly launched weapons. */
  weaponPower?: number;
  /** Live placed (not yet detonated) charges. */
  charges?: number;
  /** Cumulative pre-cracked rim parent cells (DEV / E2E). */
  preCrackedCells?: number;
  /** Last blast epicenter. */
  lastBlast?: { x: number; y: number; z: number } | null;
  /** DEV invariant monitor: total violations (0 in prod). */
  invariantViolations?: number;
  /** DEV: latest violation ring (capped). */
  invariantViolationList?: Array<{
    code: string;
    bodyId?: number;
    value: number;
    simTime: number;
  }>;
  /** DEV: avg monitor cost ms/step. */
  invariantOverheadMsPerStep?: number;
  maxLiveCells?: number;
  liveCells?: number;
  pristineCellCount?: number;
  /** D2.2 last physical resolve stats. */
  lastResolve?: {
    energy: number;
    carvedCells: number;
    carvedVolume: number;
    carvedVolumeFrac: number;
    surplusEnergy: number;
    newSurfaceArea: number;
    spentSurfaceArea: number;
    subPieces: number;
    clampedChunks: number;
    flooredChunks?: number;
    maxChunkSpeed: number;
    pulverizedVolume?: number;
  } | null;
  /** Live dynamic chunk volumes (cap 300). */
  fragmentVolumes?: number[];
  clampEngagements?: number;
  /** DEV: how often each post-solver correction actually engaged (audit F2). */
  patchEngagements?: {
    stabilizeContact: number;
    wallFaceEject: number;
    stallRecover: number;
    rubbleKick: number;
  };
  pileRadius?: number;
  pileHeight?: number;
  /** Accumulated fixed-step sim time (s) — probes gate on this, never wall clock. */
  simTime?: number;
  /** Dynamic chunks parked in the firing corridor (dead-cushion telemetry). */
  dynCorridorCount?: number;
  dynMeanY?: number;
  /** Dynamic chunks that have moved >0.15 m from their spawn centroid. */
  releasedDisplacedCount?: number;
  releasedDisplacedFrac?: number;
  /** Spikes currently stuck in structure. */
  embeddedProjectiles?: number;
  projectilePositions?: Array<{ x: number; y: number; z: number; speed: number }>;
  /** Renderer telemetry used by the Concrete Material Lab. */
  renderCalls?: number;
  renderTriangles?: number;
  renderTextures?: number;
  renderGeometries?: number;
  renderPrograms?: number;
  pixelRatio?: number;
  /** Physics and remnant timings used by the Phase 0 baseline harness. */
  stepMs?: number;
  bodyCountTotal?: number;
  staticBodyCount?: number;
  dynamicBodyCount?: number;
  fadingBodyCount?: number;
  remnantMergeMs?: number;
  remnantMergeCount?: number;
  eventRingCapacity?: number;
  eventRingHistory?: number;
  eventRingTelemetry?: {
    epoch: number;
    appended: number;
    droppedHistory: number;
    droppedPending: number;
    retainedHistoryStart: number | null;
    retainedHistoryEnd: number | null;
    pendingStart: number | null;
    pendingEnd: number | null;
  };
  damageLedgerCapacity?: number;
  damageLedgerHistory?: number;
  damageLedgerTelemetry?: {
    epoch: number;
    appended: number;
    droppedFacts: number;
    retainedStart: number | null;
    retainedEnd: number | null;
  };
  damageFacts?: ReadonlyArray<{
    sequence: number;
    simTime: number;
    kind: 'kinetic' | 'blast' | 'contact-load';
    position: { x: number; y: number; z: number };
    outwardNormal: { x: number; y: number; z: number };
    radius: number;
    appliedEnergy: number;
    normalizedSeverity: number;
    cellId?: number;
    damageBefore: number;
    damageAfter: number;
    topologyChanged: boolean;
  }>;
  /** Concrete Material Lab A/B source mode. */
  concreteSource?: ConcreteSourceMode;
  /**
   * Bounded recent kinetic-contact traces (max 8). Enough fields to build
   * ImpactObservation for the impact evaluation framework.
   */
  impactTraces?: Array<{
        id: number;
        sequence: number;
        projectileBodyId?: number;
        contactBodyId?: number;
    contactKind: 'monolith' | 'static' | 'dynamic' | 'projectile' | 'ground';
    contactPoint: { x: number; y: number; z: number };
    approachSpeed: number;
    projectileRadius: number;
    projectileMass: number;
    energyTransfer: number;
    appliedEnergy: number;
    rubbleKickSpeed?: number;
    transmitted?: true;
    embedded?: true;
    belowStructuralGate?: true;
    preferredPrimaryCellId?: number;
    damageOrigin: { x: number; y: number; z: number };
    directReleases: Array<{ id: number; position: { x: number; y: number; z: number } }>;
    supportReleases: Array<{ id: number; position: { x: number; y: number; z: number } }>;
    carvedCellCount: number;
    topologyChanged: boolean;
  }>;
  /** Last event crushed volume (m³). */
  pulverizedVolume?: number;
  /** Session sum of crushed volume; Reset clears. */
  sessionPulverizedVolume?: number;
  lastShardVolume?: number;
  lastSpawnedChunkVolume?: number;
  residueGeneration?: number;
  bakedActive?: boolean;
  bakeMs?: number;
  bakeSeq?: number;
  bakeFailed?: boolean;
  /** Orbit azimuth (rad) around the controls target — E2E / mobile-orbit probes. */
  cameraAzimuth?: number;
  cameraPolar?: number;
  cameraX?: number;
  cameraY?: number;
  cameraZ?: number;
  decalRing?: { live: number; bakedDown: number; pending?: boolean };
  bakeDownMs?: number;
  bakeDownEvents?: Array<{ simTime: number; ms: number; live: number; bakedDown: number }>;
}

interface DemolitionProbe {
  fireAt: (x: number, y: number, z: number, dirX: number, dirY: number, dirZ: number) => void;
  /** Direct fracture at a point (deterministic E2E). */
  impactAt: (
    x: number,
    y: number,
    z: number,
    speed?: number,
    dirX?: number,
    dirY?: number,
    dirZ?: number,
  ) => void;
  /** Place + immediately detonate (no fuse) — deterministic E2E blast. */
  detonateAt: (x: number, y: number, z: number) => void;
  /** Place a charge with fuse (for multi-charge volley E2E). */
  placeChargeAt: (x: number, y: number, z: number) => void;
  reset: () => void;
  /** Switch material profile; resolves when the new runtime is loaded. */
  setMaterial: (id: string) => Promise<void>;
  /** Switch tool (cannon | bomb). */
  setTool: (tool: string) => void;
  /** Set power for newly launched weapons (0.75x–1.75x). */
  setWeaponPower: (power: number) => void;
  /** DEV/e2e rest-space stability proof: spin the dynamic cell nearest a point. */
  spinDynamicChunk: (x?: number, y?: number, z?: number) => number | null;
  /** Show the Voronoi partition in place (no physics release). Continuity gate. */
  revealPartition: () => void;
  /**
   * Reframe the camera (marketing capture / E2E stills). Also retargets
   * OrbitControls so the new framing survives the next user interaction.
   * fov is the VERTICAL fov, so a portrait viewport needs a wider value than
   * the 45 that reads correctly in landscape.
   */
  setCamera: (
    px: number,
    py: number,
    pz: number,
    tx?: number,
    ty?: number,
    tz?: number,
    fov?: number,
  ) => void;
  forceBakeDown?: () => { ms: number; live: number; bakedDown: number };
  setBakeDownAuto?: (enabled: boolean) => void;
  getCamera?: () => {
    x: number;
    y: number;
    z: number;
    tx: number;
    ty: number;
    tz: number;
    azimuth: number;
    polar: number;
    enabled: boolean;
    nan: boolean;
  };
}

declare global {
  interface Window {
    __demolitionState?: DemolitionState;
    __demolitionProbe?: DemolitionProbe;
    __demolitionVisualState?: DemolitionVisualState;
  }
}

function cameraOrbitSnapshot(
  camera: THREE.Camera,
  controls: OrbitControlsImpl | null,
): Pick<DemolitionState, 'cameraAzimuth' | 'cameraPolar' | 'cameraX' | 'cameraY' | 'cameraZ'> {
  const target = controls?.target;
  const tx = target?.x ?? 0;
  const ty = target?.y ?? 0;
  const tz = target?.z ?? 0;
  const px = camera.position.x;
  const py = camera.position.y;
  const pz = camera.position.z;
  return {
    cameraX: px,
    cameraY: py,
    cameraZ: pz,
    cameraAzimuth: Math.atan2(px - tx, pz - tz),
    cameraPolar: Math.atan2(Math.hypot(px - tx, pz - tz), py - ty),
  };
}

function writeDemoState(patch: Partial<DemolitionState>) {
  const prev = window.__demolitionState;
  window.__demolitionState = {
    loaded: patch.loaded ?? prev?.loaded ?? false,
    material: patch.material ?? prev?.material ?? 'concrete',
    cellCount: patch.cellCount ?? prev?.cellCount ?? 0,
    staticCells: patch.staticCells ?? prev?.staticCells ?? 0,
    dynamicChunks: patch.dynamicChunks ?? prev?.dynamicChunks ?? 0,
    removedChunks: patch.removedChunks ?? prev?.removedChunks ?? 0,
    awake: patch.awake ?? prev?.awake ?? 0,
    fractured: patch.fractured ?? prev?.fractured ?? false,
    lastImpact: patch.lastImpact !== undefined ? patch.lastImpact : (prev?.lastImpact ?? null),
    collapsedUnsupported: patch.collapsedUnsupported ?? prev?.collapsedUnsupported ?? 0,
    fps: patch.fps ?? prev?.fps ?? 0,
    canvasReady: patch.canvasReady ?? prev?.canvasReady ?? false,
    maxDynamicY: patch.maxDynamicY ?? prev?.maxDynamicY ?? 0,
    fadingChunks: patch.fadingChunks ?? prev?.fadingChunks ?? 0,
    subChunks: patch.subChunks ?? prev?.subChunks ?? 0,
    shardParticles: patch.shardParticles ?? prev?.shardParticles ?? 0,
    minChunkVol: patch.minChunkVol ?? prev?.minChunkVol ?? 0,
    maxChunkVol: patch.maxChunkVol ?? prev?.maxChunkVol ?? 0,
    lastPartitionMs: patch.lastPartitionMs ?? prev?.lastPartitionMs ?? 0,
    lastCarveMs: patch.lastCarveMs ?? prev?.lastCarveMs ?? 0,
    tool: patch.tool ?? prev?.tool ?? 'cannon',
    projectileCount: patch.projectileCount ?? prev?.projectileCount ?? 0,
    weaponPower: patch.weaponPower ?? prev?.weaponPower ?? DEFAULT_WEAPON_POWER,
    charges: patch.charges ?? prev?.charges ?? 0,
    preCrackedCells: patch.preCrackedCells ?? prev?.preCrackedCells ?? 0,
    lastBlast: patch.lastBlast !== undefined ? patch.lastBlast : (prev?.lastBlast ?? null),
    invariantViolations: patch.invariantViolations ?? prev?.invariantViolations ?? 0,
    invariantViolationList:
      patch.invariantViolationList ?? prev?.invariantViolationList ?? [],
    invariantOverheadMsPerStep:
      patch.invariantOverheadMsPerStep ?? prev?.invariantOverheadMsPerStep ?? 0,
    maxLiveCells: patch.maxLiveCells ?? prev?.maxLiveCells ?? 0,
    lastResolve:
      patch.lastResolve !== undefined ? patch.lastResolve : (prev?.lastResolve ?? null),
    fragmentVolumes: patch.fragmentVolumes ?? prev?.fragmentVolumes ?? [],
    clampEngagements: patch.clampEngagements ?? prev?.clampEngagements ?? 0,
    patchEngagements: patch.patchEngagements ?? prev?.patchEngagements ?? {
      stabilizeContact: 0,
      wallFaceEject: 0,
      stallRecover: 0,
      rubbleKick: 0,
    },
    pileRadius: patch.pileRadius ?? prev?.pileRadius ?? 0,
    pileHeight: patch.pileHeight ?? prev?.pileHeight ?? 0,
    simTime: patch.simTime ?? prev?.simTime ?? 0,
    dynCorridorCount: patch.dynCorridorCount ?? prev?.dynCorridorCount ?? 0,
    dynMeanY: patch.dynMeanY ?? prev?.dynMeanY ?? 0,
    releasedDisplacedCount:
      patch.releasedDisplacedCount ?? prev?.releasedDisplacedCount ?? 0,
    releasedDisplacedFrac:
      patch.releasedDisplacedFrac ?? prev?.releasedDisplacedFrac ?? 0,
    embeddedProjectiles:
      patch.embeddedProjectiles ?? prev?.embeddedProjectiles ?? 0,
    projectilePositions: patch.projectilePositions ?? prev?.projectilePositions ?? [],
    liveCells: patch.liveCells ?? prev?.liveCells ?? 0,
    pristineCellCount: patch.pristineCellCount ?? prev?.pristineCellCount ?? 0,
    renderCalls: patch.renderCalls ?? prev?.renderCalls ?? 0,
    renderTriangles: patch.renderTriangles ?? prev?.renderTriangles ?? 0,
    renderTextures: patch.renderTextures ?? prev?.renderTextures ?? 0,
    renderGeometries: patch.renderGeometries ?? prev?.renderGeometries ?? 0,
    renderPrograms: patch.renderPrograms ?? prev?.renderPrograms ?? 0,
    pixelRatio: patch.pixelRatio ?? prev?.pixelRatio ?? 1,
    stepMs: patch.stepMs ?? prev?.stepMs ?? 0,
    bodyCountTotal: patch.bodyCountTotal ?? prev?.bodyCountTotal ?? 0,
    staticBodyCount: patch.staticBodyCount ?? prev?.staticBodyCount ?? 0,
    dynamicBodyCount: patch.dynamicBodyCount ?? prev?.dynamicBodyCount ?? 0,
    fadingBodyCount: patch.fadingBodyCount ?? prev?.fadingBodyCount ?? 0,
    remnantMergeMs: patch.remnantMergeMs ?? prev?.remnantMergeMs ?? 0,
    remnantMergeCount: patch.remnantMergeCount ?? prev?.remnantMergeCount ?? 0,
    eventRingCapacity: patch.eventRingCapacity ?? prev?.eventRingCapacity ?? 0,
    eventRingHistory: patch.eventRingHistory ?? prev?.eventRingHistory ?? 0,
    eventRingTelemetry: patch.eventRingTelemetry ?? prev?.eventRingTelemetry ?? {
      epoch: 0, appended: 0, droppedHistory: 0, droppedPending: 0,
      retainedHistoryStart: null, retainedHistoryEnd: null, pendingStart: null, pendingEnd: null,
    },
    damageLedgerCapacity: patch.damageLedgerCapacity ?? prev?.damageLedgerCapacity ?? 0,
    damageLedgerHistory: patch.damageLedgerHistory ?? prev?.damageLedgerHistory ?? 0,
    damageLedgerTelemetry: patch.damageLedgerTelemetry ?? prev?.damageLedgerTelemetry ?? {
      epoch: 0, appended: 0, droppedFacts: 0, retainedStart: null, retainedEnd: null,
    },
    damageFacts: patch.damageFacts ?? prev?.damageFacts ?? [],
    concreteSource: patch.concreteSource ?? prev?.concreteSource ?? 'procedural',
    impactTraces: patch.impactTraces ?? prev?.impactTraces ?? [],
    pulverizedVolume: patch.pulverizedVolume ?? prev?.pulverizedVolume ?? 0,
    sessionPulverizedVolume:
      patch.sessionPulverizedVolume ?? prev?.sessionPulverizedVolume ?? 0,
    lastShardVolume: patch.lastShardVolume ?? prev?.lastShardVolume ?? 0,
    lastSpawnedChunkVolume:
      patch.lastSpawnedChunkVolume ?? prev?.lastSpawnedChunkVolume ?? 0,
    residueGeneration: patch.residueGeneration ?? prev?.residueGeneration ?? 0,
    bakedActive: patch.bakedActive ?? prev?.bakedActive ?? false,
    bakeMs: patch.bakeMs ?? prev?.bakeMs ?? 0,
    bakeSeq: patch.bakeSeq ?? prev?.bakeSeq ?? 0,
    bakeFailed: patch.bakeFailed ?? prev?.bakeFailed ?? false,
    cameraAzimuth: patch.cameraAzimuth ?? prev?.cameraAzimuth ?? 0,
    cameraPolar: patch.cameraPolar ?? prev?.cameraPolar ?? 0,
    cameraX: patch.cameraX ?? prev?.cameraX ?? 0,
    cameraY: patch.cameraY ?? prev?.cameraY ?? 0,
    cameraZ: patch.cameraZ ?? prev?.cameraZ ?? 0,
    decalRing: patch.decalRing ?? prev?.decalRing ?? { live: 0, bakedDown: 0 },
    bakeDownMs: patch.bakeDownMs ?? prev?.bakeDownMs ?? 0,
    bakeDownEvents: patch.bakeDownEvents ?? prev?.bakeDownEvents ?? [],
  };
}

// ---------------------------------------------------------------------------
// Materials (from DestructionProfile.skin)
// ---------------------------------------------------------------------------

function useProfileMaterials(profileId: ProfileId, concreteSettings: ConcreteMaterialSettings) {
  const materials = useMemo(() => {
    const skin = DESTRUCTION_PROFILES[profileId].skin;
    const settings = { concrete: DEFAULT_CONCRETE_SETTINGS };
    const outer = createProfileMaterial(profileId, skin.outer, 'exterior', settings);
    const inner = createProfileMaterial(profileId, skin.interior, 'interior', settings);
    return { outer, inner, list: [outer, inner] as THREE.Material[] };
  }, [profileId]);

  useEffect(
    () => () => {
      materials.outer.dispose();
      materials.inner.dispose();
    },
    [materials],
  );

  useEffect(() => {
    if (profileId !== 'concrete') return;
    updateProfileMaterial(profileId, materials.outer, { concrete: concreteSettings });
    updateProfileMaterial(profileId, materials.inner, { concrete: concreteSettings });
  }, [concreteSettings, materials, profileId]);

  // Shared ambientCG maps — load once, bind into both face materials (no per-chunk clones).
  useEffect(() => {
    if (profileId !== 'concrete' || concreteSettings.source !== 'scanned-pbr') return;
    let cancelled = false;
    ensureScannedConcreteTextures()
      .then(() => {
        if (cancelled) return;
        bindScannedConcreteTextures(materials.outer);
        bindScannedConcreteTextures(materials.inner);
      })
      .catch((err: unknown) => {
        console.warn('[concrete] scanned PBR textures failed to load', err);
      });
    return () => {
      cancelled = true;
    };
  }, [materials, profileId, concreteSettings.source]);

  return materials;
}

function useDemolitionVisualMaterials(
  profileId: ProfileId,
  concreteSettings: ConcreteMaterialSettings,
  wall: { x: number; y: number; z: number },
  wallCenter: { x: number; y: number; z: number },
) {
  const procedural = useProfileMaterials(profileId, concreteSettings);
  const { gl } = useThree();
  const [handle, setHandle] = useState<DemolitionBakeHandle | null>(null);
  const handleRef = useRef<DemolitionBakeHandle | null>(null);
  const bakeTimer = useRef(0);

  const settings = useMemo(() => {
    if (profileId === 'concrete') return concreteSettings;
    return getSurface(profileId)?.defaultSettings;
  }, [profileId, concreteSettings]);
  const settingsKey = useMemo(() => JSON.stringify(settings ?? null), [settings]);

  useEffect(() => {
    if (!isBakedProfile(profileId)) {
      handleRef.current?.dispose();
      handleRef.current = null;
      setHandle(null);
      demoBakeEnd({ ready: true, bakedActive: false, bakeMs: 0, bakeFailed: false });
      writeDemoState({ bakedActive: false, bakeMs: 0, bakeFailed: false });
      return;
    }
    let cancelled = false;
    const run = () => {
      bakeTimer.current = 0;
      demoBakeBegin();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (cancelled) {
            demoBakeEnd({});
            return;
          }
          try {
            const next = bakeDemolitionProfile(gl, profileId, settings, {
              width: wall.x,
              height: wall.y,
              thickness: wall.z,
              position: new THREE.Vector3(wallCenter.x, wallCenter.y, wallCenter.z),
            });
            if (cancelled) {
              next.dispose();
              demoBakeEnd({});
              return;
            }
            handleRef.current?.dispose();
            handleRef.current = next;
            setHandle(next);
            const seq = demoBakeUi.bakeSeq + 1;
            demoBakeEnd({
              ready: true,
              bakedActive: true,
              bakeMs: next.bakeMs,
              bakeSeq: seq,
              bakeFailed: false,
            });
            writeDemoState({
              bakedActive: true,
              bakeMs: next.bakeMs,
              bakeSeq: seq,
              bakeFailed: false,
            });
          } catch (err) {
            console.error('[demolition] bake failed', err);
            handleRef.current?.dispose();
            handleRef.current = null;
            setHandle(null);
            demoBakeEnd({ ready: true, bakedActive: false, bakeMs: 0, bakeFailed: true });
            writeDemoState({ bakedActive: false, bakeFailed: true, canvasReady: true });
          }
        }),
      );
    };
    if (handleRef.current) {
      if (bakeTimer.current) window.clearTimeout(bakeTimer.current);
      bakeTimer.current = window.setTimeout(run, BAKE_DEBOUNCE_MS);
    } else {
      run();
    }
    return () => {
      cancelled = true;
      if (bakeTimer.current) window.clearTimeout(bakeTimer.current);
    };
  }, [
    gl,
    profileId,
    settings,
    settingsKey,
    wall.x,
    wall.y,
    wall.z,
    wallCenter.x,
    wallCenter.y,
    wallCenter.z,
  ]);

  useEffect(
    () => () => {
      handleRef.current?.dispose();
      handleRef.current = null;
    },
    [],
  );

  if (handle) {
    return {
      list: handle.list as THREE.Material[],
      baked: true,
      atlas: handle.atlas,
      ready: true,
      handle,
    };
  }
  if (!isBakedProfile(profileId)) {
    return {
      list: procedural.list,
      baked: false,
      atlas: null as BakedAtlasMaps | null,
      ready: true,
      handle: null as DemolitionBakeHandle | null,
    };
  }
  return {
    list: [] as THREE.Material[],
    baked: false,
    atlas: null as BakedAtlasMaps | null,
    ready: false,
    handle: null as DemolitionBakeHandle | null,
  };
}

function cellGeomOptions(runtime: DemolitionRuntime, atlas: BakedAtlasMaps | null): CellGeometryOptions {
  return {
    wall: { width: runtime.wallFull.x, height: runtime.wallFull.y, thickness: runtime.wallFull.z },
    atlas: atlas?.regions as AtlasRegions | undefined,
    interiorTileMeters: INTERIOR_TILE_METERS,
  };
}

/** Live placed charges — dark sphere + blinking red emissive dot. */
function ChargeMarkers({ runtime }: { runtime: DemolitionRuntime }) {
  const group = useRef<THREE.Group>(null);
  const blink = useRef(0);

  useFrame((_, dt) => {
    blink.current += dt;
    const g = group.current;
    if (!g) return;
    // Rebuild children from live charges each frame (≤3).
    const live = runtime.charges.filter((c) => !c.detonated);
    while (g.children.length > live.length) {
      const child = g.children[g.children.length - 1];
      g.remove(child);
      child.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
        if (m.material) {
          const mat = m.material as THREE.Material | THREE.Material[];
          if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
          else mat.dispose();
        }
      });
    }
    while (g.children.length < live.length) {
      const shell = new THREE.Mesh(
        new THREE.SphereGeometry(0.09, 12, 10),
        new THREE.MeshStandardMaterial({ color: '#1a1a1c', roughness: 0.7, metalness: 0.35 }),
      );
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.028, 8, 8),
        new THREE.MeshStandardMaterial({
          color: '#ff1a1a',
          emissive: '#ff0000',
          emissiveIntensity: 1.4,
          roughness: 0.4,
          metalness: 0.1,
        }),
      );
      dot.position.set(0, 0.07, 0);
      shell.add(dot);
      g.add(shell);
    }
    const pulse = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(blink.current * 14));
    for (let i = 0; i < live.length; i += 1) {
      const c = live[i];
      const shell = g.children[i] as THREE.Mesh;
      shell.position.set(c.x, c.y, c.z);
      const dot = shell.children[0] as THREE.Mesh;
      const mat = dot.material as THREE.MeshStandardMaterial;
      mat.emissiveIntensity = 0.6 + pulse * 1.6;
    }
  });

  return <group ref={group} />;
}

// ---------------------------------------------------------------------------
// Wall visuals
// ---------------------------------------------------------------------------

function IntactWall({
  runtime,
  materials,
  atlas,
}: {
  runtime: DemolitionRuntime;
  materials: THREE.Material[];
  atlas: BakedAtlasMaps | null;
}) {
  const { wallCenter, wallFull } = runtime;
  const geometry = useMemo(() => {
    if (atlas) {
      const next = new THREE.BoxGeometry(wallFull.x, wallFull.y, wallFull.z);
      addSurfaceRestPositionAttribute(next);
      assignWallBoxAtlasUvs(
        next,
        { width: wallFull.x, height: wallFull.y, thickness: wallFull.z },
        atlas.regions as AtlasRegions,
      );
      try {
        next.computeTangents();
      } catch {
        // Box faces always have a tangent frame; keep going if a runtime lacks it.
      }
      return next;
    }
    // A real cast slab has a small radius on exposed edges. The bevel is kept
    // below the scale of the wall so it catches highlights without changing
    // the demolition silhouette or fracture dimensions.
    const next = new RoundedBoxGeometry(wallFull.x, wallFull.y, wallFull.z, 2, 0.025);
    addSurfaceRestPositionAttribute(next);
    return next;
  }, [atlas, wallFull.x, wallFull.y, wallFull.z]);

  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <mesh
      geometry={geometry}
      position={[wallCenter.x, wallCenter.y, wallCenter.z]}
      castShadow
      receiveShadow
      material={materials[0]}
    />
  );
}

type RemnantBatches = {
  outer: THREE.BatchedMesh;
  inner: THREE.BatchedMesh;
  outerInstances: Map<number, number>;
  innerInstances: Map<number, number>;
};

function recordRemnantTiming(runtime: DemolitionRuntime, elapsedMs: number) {
  runtime.remnantMergeMs = elapsedMs;
  runtime.remnantMergeCount += 1;
}

function buildRemnantBatches(
  runtime: DemolitionRuntime,
  materials: THREE.Material[],
  geomOpts: CellGeometryOptions,
): RemnantBatches {
  const buildStarted = performance.now();
  const cells = runtime.pristineCells;
  const sources = cells.map((cell) => cellToBufferGeometry(cell, geomOpts));
  const outerIndexCount = sources.reduce((sum, source) => sum + (source.groups[0]?.count ?? 0), 0);
  const innerIndexCount = sources.reduce((sum, source) => sum + (source.groups[1]?.count ?? 0), 0);
  const vertexCount = sources.reduce((sum, source) => sum + source.getAttribute('position').count, 0);
  const outer = new THREE.BatchedMesh(
    Math.max(1, cells.length),
    Math.max(1, vertexCount),
    Math.max(1, outerIndexCount),
    materials[0],
  );
  const inner = new THREE.BatchedMesh(
    Math.max(1, cells.length),
    Math.max(1, vertexCount),
    Math.max(1, innerIndexCount),
    materials[1],
  );
  const outerInstances = new Map<number, number>();
  const innerInstances = new Map<number, number>();
  const translation = new THREE.Matrix4();
  const addGroup = (
    batch: THREE.BatchedMesh,
    source: THREE.BufferGeometry,
    group: { start: number; count: number } | undefined,
    instances: Map<number, number>,
    cellId: number,
    cell: FractureCell,
  ) => {
    if (!group || group.count <= 0) return;
    const index = source.getIndex();
    if (!index) return;
    const split = source.clone();
    split.clearGroups();
    split.setIndex(Array.from(index.array.slice(group.start, group.start + group.count)));
    const geometryId = batch.addGeometry(split);
    const instanceId = batch.addInstance(geometryId);
    translation.makeTranslation(
      runtime.wallCenter.x + cell.centroid.x,
      runtime.wallCenter.y + cell.centroid.y,
      runtime.wallCenter.z + cell.centroid.z,
    );
    batch.setMatrixAt(instanceId, translation);
    batch.setVisibleAt(instanceId, true);
    instances.set(cellId, instanceId);
    split.dispose();
  };
  for (let index = 0; index < cells.length; index += 1) {
    const cell = cells[index];
    const source = sources[index];
    addGroup(outer, source, source.groups[0], outerInstances, cell.id, cell);
    addGroup(inner, source, source.groups[1], innerInstances, cell.id, cell);
    source.dispose();
  }
  outer.castShadow = true;
  outer.receiveShadow = true;
  inner.castShadow = true;
  inner.receiveShadow = true;
  outer.frustumCulled = false;
  inner.frustumCulled = false;
  outer.computeBoundingBox();
  outer.computeBoundingSphere();
  inner.computeBoundingBox();
  inner.computeBoundingSphere();
  recordRemnantTiming(runtime, performance.now() - buildStarted);
  return { outer, inner, outerInstances, innerInstances };
}

function patchRemnantVisibility(runtime: DemolitionRuntime, batches: RemnantBatches) {
  const patchStarted = performance.now();
  for (const cell of runtime.pristineCells) {
    const visible = runtime.records.get(cell.id)?.state === 'static';
    const outerInstance = batches.outerInstances.get(cell.id);
    const innerInstance = batches.innerInstances.get(cell.id);
    if (outerInstance !== undefined) batches.outer.setVisibleAt(outerInstance, visible);
    if (innerInstance !== undefined) batches.inner.setVisibleAt(innerInstance, visible);
  }
  runtime.remnantDirty = false;
  recordRemnantTiming(runtime, performance.now() - patchStarted);
}

function FracturedWall({
  runtime,
  materials,
  wallVersion,
  atlas,
}: {
  runtime: DemolitionRuntime;
  materials: THREE.Material[];
  wallVersion: number;
  atlas: BakedAtlasMaps | null;
}) {
  const chunkGeoms = useRef<Map<number, THREE.BufferGeometry>>(new Map());
  const chunkMeshes = useRef<Map<number, THREE.Mesh>>(new Map());
  /** Per-chunk cloned materials used only while fading (shared mats stay opaque). */
  const fadeMats = useRef<Map<number, THREE.Material[]>>(new Map());
  const groupRef = useRef<THREE.Group>(null);
  const geomOpts = useMemo(() => cellGeomOptions(runtime, atlas), [runtime, atlas]);

  // Build per-cell geometries once (and whenever the partition regenerates — glass).
  useEffect(() => {
    const map = new Map<number, THREE.BufferGeometry>();
    for (const cell of runtime.cells) {
      map.set(cell.id, cellToBufferGeometry(cell, geomOpts));
    }
    chunkGeoms.current = map;
    // Drop live meshes so they rebuild against the new geom map.
    for (const mesh of chunkMeshes.current.values()) {
      mesh.parent?.remove(mesh);
    }
    chunkMeshes.current.clear();
    return () => {
      for (const g of map.values()) g.dispose();
      map.clear();
      for (const mats of fadeMats.current.values()) {
        for (const m of mats) m.dispose();
      }
      fadeMats.current.clear();
    };
  }, [geomOpts, runtime.cells, runtime.wallVersion, runtime.profile.id]);

  // Phase 0 chunk strategy adoption: two single-material BatchedMesh objects
  // (outer and interior groups) retain every pristine cell and patch only
  // instance visibility as the static remnant changes. This avoids rebuilding
  // one large geometry after every carve while preserving the existing two
  // material appearance and per-cell rest-space attributes.
  const remnantBatches = useMemo(() => buildRemnantBatches(runtime, materials, geomOpts), [
    geomOpts,
    materials,
    runtime,
    runtime.pristineCells,
    runtime.profile.id,
    runtime.wallCenter.x,
    runtime.wallCenter.y,
    runtime.wallCenter.z,
  ]);

  useEffect(() => {
    patchRemnantVisibility(runtime, remnantBatches);
  }, [remnantBatches, runtime, wallVersion]);

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;

    // Repaint remnant visibility from the frame loop, driven by the runtime's own
    // dirty flag. The React effect above only fires when `wallVersion` changes,
    // i.e. on a carve — but cells are also released by staggered support collapse
    // and pre-crack, which set `remnantDirty` WITHOUT bumping the version. Those
    // releases left the batch still drawing cells that were simultaneously
    // falling as chunks: the wall looked intact while parts rained out of it.
    if (runtime.remnantDirty) patchRemnantVisibility(runtime, remnantBatches);

    // Sync dynamic chunk meshes to transforms.
    const needed = new Set<number>();
    for (const rec of runtime.records.values()) {
      const isFading = rec.state === 'removed' && rec.fade >= 0 && rec.fade < 1;
      // Static cells minted at runtime (pre-crack children) have no instance in
      // the pristine batch, so without their own mesh they are invisible while
      // still being solid, collidable wall.
      const isUninstancedStatic =
        rec.state === 'static' && !remnantBatches.outerInstances.has(rec.cellId);
      if (rec.state !== 'dynamic' && !isFading && !isUninstancedStatic) {
        continue;
      }
      needed.add(rec.cellId);
      let mesh = chunkMeshes.current.get(rec.cellId);
      if (!mesh) {
        // On-demand geom for runtime sub-chunks (not in the load-time map).
        let geom = chunkGeoms.current.get(rec.cellId);
        if (!geom) {
          const cell = runtime.cellById.get(rec.cellId);
          if (!cell) continue;
          geom = cellToBufferGeometry(cell, geomOpts);
          chunkGeoms.current.set(rec.cellId, geom);
        }
        // Live chunks always share the opaque material pair — never transparent.
        mesh = new THREE.Mesh(geom, materials);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        group.add(mesh);
        chunkMeshes.current.set(rec.cellId, mesh);
      }
      const offset = cellTransformOffset(runtime, rec.cellId);
      if (offset == null) continue;
      const t = runtime.transforms;
      mesh.position.set(t[offset], t[offset + 1], t[offset + 2]);
      mesh.quaternion.set(t[offset + 3], t[offset + 4], t[offset + 5], t[offset + 6]);
      mesh.scale.setScalar(1);

      if (rec.state === 'removed' && rec.fade >= 0) {
        // Clone materials only while fading so opacity never ghosts siblings.
        let mats = fadeMats.current.get(rec.cellId);
        if (!mats) {
          mats = materials.map((m) => {
            const c = cloneProfileMaterial(m);
            c.transparent = true;
            c.depthWrite = false;
            c.opacity = 1;
            return c;
          });
          fadeMats.current.set(rec.cellId, mats);
          mesh.material = mats;
        }
        const opacity = Math.max(0, 1 - rec.fade);
        for (const m of mats) {
          m.opacity = opacity;
        }
        mesh.visible = opacity > 0.02;
      } else {
        // Ensure live dynamics stay on shared opaque mats (recover if ever swapped).
        if (mesh.material !== materials) {
          mesh.material = materials;
        }
        mesh.visible = true;
        const leftover = fadeMats.current.get(rec.cellId);
        if (leftover) {
          for (const m of leftover) m.dispose();
          fadeMats.current.delete(rec.cellId);
        }
      }
    }

    // Remove meshes no longer needed.
    for (const [id, mesh] of chunkMeshes.current) {
      if (!needed.has(id)) {
        group.remove(mesh);
        chunkMeshes.current.delete(id);
        const mats = fadeMats.current.get(id);
        if (mats) {
          for (const m of mats) m.dispose();
          fadeMats.current.delete(id);
        }
      }
    }
  });

  return (
    <group>
      <primitive object={remnantBatches.outer} />
      <primitive object={remnantBatches.inner} />
      <group ref={groupRef} />
    </group>
  );
}

// ---------------------------------------------------------------------------
// Cannonballs
// ---------------------------------------------------------------------------

const PROJECTILE_VISUALS: Record<DemoTool, { scale: [number, number, number]; color: string }> = {
  cannon: { scale: [1.05, 1.05, 1.05], color: '#414851' },
  bomb: { scale: [0.92, 0.92, 0.92], color: '#ef9a2d' },
  spike: { scale: [0.82, 0.82, 1.1], color: '#d9f0ff' },
  rubble: { scale: [1.18, 0.9, 1.08], color: '#a47758' },
  concussion: { scale: [0.88, 0.88, 1.15], color: '#55c8ff' },
};

const PROJECTILE_TOOLS: DemoTool[] = ['cannon', 'bomb', 'spike', 'rubble', 'concussion'];

function Cannonballs({ runtime }: { runtime: DemolitionRuntime }) {
  const meshRefs = useRef<Record<DemoTool, THREE.InstancedMesh | null>>({
    cannon: null,
    bomb: null,
    spike: null,
    rubble: null,
    concussion: null,
  });
  const trailRef = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const trailDummy = useMemo(() => new THREE.Object3D(), []);
  const trailColor = useMemo(() => new THREE.Color(), []);
  const trailTint = useMemo(() => new THREE.Color('#fff1bc'), []);
  // Cone/cylinder projectile geometry is authored along local +Y.
  const travelAxis = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const trailAxis = useMemo(() => new THREE.Vector3(0, 0, 1), []);
  const travelDirection = useMemo(() => new THREE.Vector3(), []);

  useFrame(() => {
    const trail = trailRef.current;
    if (!trail) return;
    const n = runtime.cannonballs.length;

    // Each weapon gets its own six-instance pool so silhouette and material
    // remain readable without ever indexing beyond the fixed projectile cap.
    for (const weapon of PROJECTILE_TOOLS) {
      const mesh = meshRefs.current[weapon];
      if (!mesh) continue;
      for (let i = 0; i < 6; i += 1) {
        dummy.position.set(0, -100, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      }
    }
    for (let i = 0; i < 6; i += 1) {
      trailDummy.position.set(0, -100, 0);
      trailDummy.scale.setScalar(0);
      trailDummy.updateMatrix();
      trail.setMatrixAt(i, trailDummy.matrix);
    }

    for (let i = 0; i < 6; i += 1) {
      if (i < n) {
        const o = cannonballTransformOffset(runtime, i);
        const t = runtime.transforms;
        if (o + 6 < t.length) {
          const weapon = projectileToolAt(runtime, i);
          const visual = PROJECTILE_VISUALS[weapon];
          const mesh = meshRefs.current[weapon];
          const power = projectilePowerAt(runtime, i);
          if (!mesh) continue;
          dummy.position.set(t[o], t[o + 1], t[o + 2]);
          dummy.quaternion.identity();
          dummy.scale.set(
            visual.scale[0] * power,
            visual.scale[1] * power,
            visual.scale[2] * power,
          );
          let distance = 0;

          // Keep a short, physical-looking streak between the last two
          // sampled poses. The sphere remains the head of the throw, while
          // this makes a fast wall-crossing shot readable on a phone.
          if (runtime.prevValid && o + 6 < runtime.prevTransforms.length) {
            const px = runtime.prevTransforms[o];
            const py = runtime.prevTransforms[o + 1];
            const pz = runtime.prevTransforms[o + 2];
            const dx = t[o] - px;
            const dy = t[o + 1] - py;
            const dz = t[o + 2] - pz;
            distance = Math.hypot(dx, dy, dz);
            if (distance > 0.015 && Number.isFinite(distance)) {
              travelDirection.set(dx / distance, dy / distance, dz / distance);
              dummy.quaternion.setFromUnitVectors(travelAxis, travelDirection);
              dummy.updateMatrix();
              mesh.setMatrixAt(i, dummy.matrix);
              trailDummy.position.set((t[o] + px) * 0.5, (t[o + 1] + py) * 0.5, (t[o + 2] + pz) * 0.5);
              trailDummy.quaternion.setFromUnitVectors(trailAxis, travelDirection);
              trailDummy.scale.set(
                visual.scale[0] * power * 0.42,
                visual.scale[1] * power * 0.42,
                Math.min(4.5, distance / CANNONBALL_RADIUS),
              );
              trailDummy.updateMatrix();
              trail.setMatrixAt(i, trailDummy.matrix);
              trailColor.set(visual.color).lerp(trailTint, 0.46);
              trail.setColorAt(i, trailColor);
            } else {
              trailDummy.position.set(0, -100, 0);
              trailDummy.scale.setScalar(0);
              trailDummy.updateMatrix();
              trail.setMatrixAt(i, trailDummy.matrix);
            }
          }
          if (distance <= 0.015 || !Number.isFinite(distance)) {
            dummy.updateMatrix();
            mesh.setMatrixAt(i, dummy.matrix);
          }
        }
      }
    }
    for (const weapon of PROJECTILE_TOOLS) {
      const mesh = meshRefs.current[weapon];
      if (!mesh) continue;
      mesh.instanceMatrix.needsUpdate = true;
    }
    trail.instanceMatrix.needsUpdate = true;
    if (trail.instanceColor) trail.instanceColor.needsUpdate = true;
  });

  return (
    <group>
      <instancedMesh ref={trailRef} args={[undefined, undefined, 6]} frustumCulled={false}>
        <sphereGeometry args={[CANNONBALL_RADIUS, 12, 8]} />
        <meshBasicMaterial vertexColors transparent opacity={0.54} depthWrite={false} />
      </instancedMesh>
      <instancedMesh ref={(mesh) => { meshRefs.current.cannon = mesh; }} args={[undefined, undefined, 6]} castShadow frustumCulled={false}>
        <sphereGeometry args={[CANNONBALL_RADIUS, 16, 12]} />
        <meshStandardMaterial color={PROJECTILE_VISUALS.cannon.color} roughness={0.32} metalness={0.72} />
      </instancedMesh>
      <instancedMesh ref={(mesh) => { meshRefs.current.bomb = mesh; }} args={[undefined, undefined, 6]} castShadow frustumCulled={false}>
        <dodecahedronGeometry args={[CANNONBALL_RADIUS * 1.05, 1]} />
        <meshStandardMaterial color={PROJECTILE_VISUALS.bomb.color} roughness={0.34} metalness={0.5} />
      </instancedMesh>
      <instancedMesh ref={(mesh) => { meshRefs.current.spike = mesh; }} args={[undefined, undefined, 6]} castShadow frustumCulled={false}>
        <coneGeometry args={[CANNONBALL_RADIUS * 0.72, CANNONBALL_RADIUS * 3.4, 8]} />
        <meshStandardMaterial color={PROJECTILE_VISUALS.spike.color} roughness={0.24} metalness={0.82} />
      </instancedMesh>
      <instancedMesh ref={(mesh) => { meshRefs.current.rubble = mesh; }} args={[undefined, undefined, 6]} castShadow frustumCulled={false}>
        <icosahedronGeometry args={[CANNONBALL_RADIUS * 1.1, 0]} />
        <meshStandardMaterial color={PROJECTILE_VISUALS.rubble.color} roughness={0.92} metalness={0.04} flatShading />
      </instancedMesh>
      <instancedMesh ref={(mesh) => { meshRefs.current.concussion = mesh; }} args={[undefined, undefined, 6]} castShadow frustumCulled={false}>
        <cylinderGeometry args={[CANNONBALL_RADIUS * 0.72, CANNONBALL_RADIUS * 0.72, CANNONBALL_RADIUS * 2.4, 12]} />
        <meshStandardMaterial color={PROJECTILE_VISUALS.concussion.color} roughness={0.3} metalness={0.42} />
      </instancedMesh>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Ground + lights
// ---------------------------------------------------------------------------

function Ground() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
      <planeGeometry args={[80, 80]} />
      <meshStandardMaterial color="#4a535e" roughness={0.9} metalness={0.02} />
    </mesh>
  );
}

function setSceneEnvironment(
  scene: THREE.Scene,
  environment: THREE.Texture | null,
  intensity: number,
) {
  scene.environment = environment;
  scene.environmentIntensity = intensity;
}

/**
 * Local deterministic PMREM studio environment (RoomEnvironment — no network).
 * Reveals roughness/normal response for MeshStandard materials without replacing
 * the dramatic directional light rig.
 */
function DemoStudioEnvironment() {
  const { gl, scene } = useThree();

  useEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    const room = new RoomEnvironment();
    const envRT = pmrem.fromScene(room, 0.04);
    const previousEnv = scene.environment;
    const previousIntensity = scene.environmentIntensity;
    // Keep PMREM for roughness/normal response, but let the explicit cool key
    // rig own the read so the scanned albedo is not pushed toward cream.
    setSceneEnvironment(scene, envRT.texture, 0.42);
    room.dispose();

    return () => {
      setSceneEnvironment(scene, previousEnv, previousIntensity);
      envRT.dispose();
      pmrem.dispose();
    };
  }, [gl, scene]);

  return null;
}

function SceneLights({ mode }: { mode: ConcreteLightingMode }) {
  // Cool-neutral look-dev rig: concrete stays middle-gray with readable
  // formwork/grain while each lighting mode keeps its own contrast story.
  const lighting = {
    dramatic: {
      ambient: 0.14,
      hemisphere: 0.2,
      key: 1.28,
      fill: 0.12,
      rim: 0.2,
      keyColor: '#e5edf7',
      fillColor: '#83a8d1',
    },
    neutral: {
      ambient: 0.18,
      hemisphere: 0.24,
      key: 1.02,
      fill: 0.12,
      rim: 0.14,
      keyColor: '#e8edf3',
      fillColor: '#b9c9dc',
    },
    overcast: {
      ambient: 0.3,
      hemisphere: 0.34,
      key: 0.5,
      fill: 0.1,
      rim: 0.08,
      keyColor: '#c9d7e6',
      fillColor: '#92a8c2',
    },
    wet: {
      ambient: 0.16,
      hemisphere: 0.24,
      key: 1.12,
      fill: 0.1,
      rim: 0.32,
      keyColor: '#c7ddfa',
      fillColor: '#638bc0',
    },
  }[mode];

  return (
    <>
      {/* Mild ambient + hemisphere so flat interior normals still read as
          light fresh-break concrete instead of pure black. Kept low enough that
          PMREM + key light do not wash scanned albedo. */}
      <ambientLight intensity={lighting.ambient} />
      <hemisphereLight args={['#d4e4f4', '#161c25', lighting.hemisphere]} />
      <directionalLight
        position={[8, 14, 6]}
        intensity={lighting.key}
        color={lighting.keyColor}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-far={40}
        shadow-camera-left={-12}
        shadow-camera-right={12}
        shadow-camera-top={12}
        shadow-camera-bottom={-4}
      />
      <directionalLight position={[-6, 6, -8]} intensity={lighting.fill} color={lighting.fillColor} />
      {/* Soft fill from below/front so hole interiors aren't key-light only. */}
      <directionalLight position={[0, 2, 10]} intensity={lighting.fill * 0.55} color="#9fb5cc" />
      <directionalLight position={[0, 8, -10]} intensity={lighting.rim} color="#dceaff" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Interaction + simulation loop
// ---------------------------------------------------------------------------

function DemoSim({
  runtimeRef,
  sessionRef,
  statsRef,
  fxRef,
  particleRef,
  residueRef,
  controlsRef,
  onWallVersion,
  setMaterialRef,
  toolRef,
  weaponPowerRef,
  onFired,
  prewarmed,
  effectTier,
}: {
  runtimeRef: MutableRefObject<DemolitionRuntime | null>;
  sessionRef: MutableRefObject<DemolitionPageSession | null>;
  statsRef: MutableRefObject<{ fps: number; staticCells: number; dynamicChunks: number; awake: number }>;
  fxRef: MutableRefObject<DemolitionFxController | null>;
  particleRef: MutableRefObject<DemolitionParticleController | null>;
  residueRef: MutableRefObject<PowderResidueController | null>;
  controlsRef: MutableRefObject<OrbitControlsImpl | null>;
  onWallVersion: (v: number) => void;
  setMaterialRef: MutableRefObject<((id: ProfileId) => Promise<void>) | null>;
  toolRef: MutableRefObject<DemoTool>;
  weaponPowerRef: MutableRefObject<number>;
  onFired?: () => void;
  prewarmed: boolean;
  effectTier: DemolitionEffectTier;
}) {
  const { camera, gl } = useThree();
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const pointerNdc = useMemo(() => new THREE.Vector2(), []);
  const lastWallVersion = useRef(0);
  const onFiredRef = useRef(onFired);
  onFiredRef.current = onFired;

  // Pointer: left-drag = orbit (OrbitControls), right-click = fire/place (desktop).
  // Touch: short single-finger tap = fire/place; drag stays orbit; pinch = zoom/pan.
  useEffect(() => {
    const canvas = gl.domElement;
    // Instant touch response — no 300ms click delay. R3F usually sets this;
    // reaffirm so mobile fire never waits.
    canvas.style.touchAction = 'none';

    type TapCandidate = { id: number; x: number; y: number; t: number };
    let tapCandidate: TapCandidate | null = null;
    const activePointers = new Set<number>();

    const actFromClient = (clientX: number, clientY: number) => {
      const runtime = runtimeRef.current;
      const session = sessionRef.current;
      if (!runtime || !session) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      pointerNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      pointerNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointerNdc, camera);
      const tool = toolRef.current;
      const dir = raycaster.ray.direction.clone().normalize();
      const origin = (camera.position as THREE.Vector3).clone().addScaledVector(dir, 2.0);
      session.fireAt(
        { x: origin.x + dir.x * 8, y: origin.y + dir.y * 8, z: origin.z + dir.z * 8 },
        { x: dir.x, y: dir.y, z: dir.z },
        tool,
      );
      onFiredRef.current?.();
    };

    const onPointerDown = (event: PointerEvent) => {
      activePointers.add(event.pointerId);

      // Desktop: right-click fires / places immediately.
      if (event.button === 2) {
        actFromClient(event.clientX, event.clientY);
        tapCandidate = null;
        return;
      }

      // Touch: arm a tap candidate. Multi-touch (pinch/pan) cancels fire.
      if (event.pointerType === 'touch') {
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch {
          // Capture can throw if the pointer is already gone.
        }
        if (activePointers.size > 1) {
          tapCandidate = null;
          return;
        }
        tapCandidate = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          t: performance.now(),
        };
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      activePointers.delete(event.pointerId);
      const cand = tapCandidate;
      if (!cand || cand.id !== event.pointerId) return;
      tapCandidate = null;
      // Still another finger down → not a single-finger tap.
      if (activePointers.size > 0) return;
      const dt = performance.now() - cand.t;
      const dist = Math.hypot(event.clientX - cand.x, event.clientY - cand.y);
      if (dt <= TAP_MAX_MS && dist <= TAP_MAX_PX) {
        actFromClient(event.clientX, event.clientY);
      }
    };

    const flushOrbitPointer = (event: PointerEvent) => {
      // three-stdlib OrbitControls never listens for pointercancel. A cancelled
      // finger stays in its pointers[] and 3+ ghosts force STATE.NONE — orbit
      // dies. Synthesize the pointerup it already handles.
      document.dispatchEvent(
        new PointerEvent('pointerup', {
          bubbles: true,
          cancelable: true,
          pointerId: event.pointerId,
          pointerType: event.pointerType,
          isPrimary: event.isPrimary,
          clientX: event.clientX,
          clientY: event.clientY,
          button: 0,
          buttons: 0,
        }),
      );
    };

    const onPointerCancel = (event: PointerEvent) => {
      activePointers.delete(event.pointerId);
      if (tapCandidate?.id === event.pointerId) tapCandidate = null;
      flushOrbitPointer(event);
    };

    const onContextMenu = (event: MouseEvent) => event.preventDefault();
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerCancel);
    document.addEventListener('pointercancel', onPointerCancel);
    canvas.addEventListener('contextmenu', onContextMenu);
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerCancel);
      document.removeEventListener('pointercancel', onPointerCancel);
      canvas.removeEventListener('contextmenu', onContextMenu);
    };
  }, [camera, gl, pointerNdc, raycaster, runtimeRef, sessionRef, toolRef]);

  // DEV / E2E bridge. Normal simulation/input is session-owned; camera,
  // partition reveal, spin, and bake controls below are diagnostics only.
  useEffect(() => {
    // Expose runtime for golden transform hashing (DEV/e2e only).
    (window as unknown as { __demolitionRuntime?: DemolitionRuntime }).__demolitionRuntime =
      runtimeRef.current ?? undefined;
    window.__demolitionProbe = {
      fireAt(x, y, z, dirX, dirY, dirZ) {
        const session = sessionRef.current;
        if (!session) return;
        session.fireAt({ x, y, z }, { x: dirX, y: dirY, z: dirZ });
      },
      impactAt(x, y, z, speed = 25, dirX?: number, dirY?: number, dirZ?: number) {
        const session = sessionRef.current;
        const rt = runtimeRef.current;
        if (!rt || !session) return;
        const hasDir =
          typeof dirX === 'number' &&
          typeof dirY === 'number' &&
          typeof dirZ === 'number' &&
          Number.isFinite(dirX) &&
          Number.isFinite(dirY) &&
          Number.isFinite(dirZ);
        session.impactAt({ x, y, z }, speed, hasDir ? { x: dirX, y: dirY, z: dirZ } : undefined);
      },
      detonateAt(x, y, z) {
        const session = sessionRef.current;
        if (!session) return;
        session.detonateAt({ x, y, z });
      },
      placeChargeAt(x, y, z) {
        const session = sessionRef.current;
        if (!session) return;
        session.placeChargeAt({ x, y, z });
      },
      setCamera(px, py, pz, tx = 0, ty = 1.6, tz = 0, fov) {
        camera.position.set(px, py, pz);
        if (typeof fov === 'number' && camera instanceof THREE.PerspectiveCamera) {
          camera.fov = fov;
          camera.updateProjectionMatrix();
        }
        camera.lookAt(tx, ty, tz);
        // Retarget OrbitControls too, or its stored target snaps the camera
        // back the moment anything calls update().
        const controls = controlsRef.current;
        if (controls) {
          controls.target.set(tx, ty, tz);
          controls.update();
        }
      },
      reset() {
        sessionRef.current?.reset();
      },
      revealPartition() {
        const rt = runtimeRef.current;
        if (!rt) return;
        for (const cell of rt.pristineCells) {
          if (rt.records.has(cell.id)) continue;
          rt.records.set(cell.id, {
            cellId: cell.id,
            bodyId: 0,
            state: 'static',
            bufferIndex: -1,
            dynamicOrder: -1,
            worldCentroid: {
              x: rt.wallCenter.x + cell.centroid.x,
              y: rt.wallCenter.y + cell.centroid.y,
              z: rt.wallCenter.z + cell.centroid.z,
            },
            halfProxy: { x: 0.05, y: 0.05, z: 0.05 },
            volume: cell.volume,
            mass: 0,
            fade: -1,
            isSubChunk: false,
            preCrackGen: 0,
            damage: 0,
          });
        }
        rt.staticCells = rt.pristineCells.length;
        rt.fractured = true;
        rt.remnantDirty = true;
        writeDemoState({ fractured: true, staticCells: rt.staticCells });
      },
      async setMaterial(id: string) {
        const fn = setMaterialRef.current;
        if (!fn) throw new Error('setMaterial not ready');
        await fn(id as ProfileId);
      },
      setTool(tool: string) {
        const supported: DemoTool[] = ['cannon', 'bomb', 'spike', 'rubble', 'concussion'];
        const t: DemoTool = supported.includes(tool as DemoTool) ? (tool as DemoTool) : 'cannon';
        toolRef.current = t;
        sessionRef.current?.setTool(t);
        writeDemoState({ tool: t });
      },
      setWeaponPower(power: number) {
        const session = sessionRef.current;
        if (!session) return;
        session.setWeaponPower(power);
        weaponPowerRef.current = session.weaponPower;
        writeDemoState({ weaponPower: session.weaponPower });
      },
      getCamera() {
        const target = controlsRef.current?.target;
        const tx = target?.x ?? 0;
        const ty = target?.y ?? 0;
        const tz = target?.z ?? 0;
        const px = camera.position.x;
        const py = camera.position.y;
        const pz = camera.position.z;
        const dx = px - tx;
        const dy = py - ty;
        const dz = pz - tz;
        return {
          x: px,
          y: py,
          z: pz,
          tx,
          ty,
          tz,
          azimuth: Math.atan2(dx, dz),
          polar: Math.atan2(Math.hypot(dx, dz), dy),
          enabled: controlsRef.current?.enabled ?? true,
          nan: ![px, py, pz, tx, ty, tz].every(Number.isFinite),
        };
      },
      spinDynamicChunk(x = 0, y = 0, z = 0.3) {
        const rt = runtimeRef.current;
        return rt ? spinDynamicChunk(rt, { x, y, z }) : null;
      },
      forceBakeDown() {
        const rt = runtimeRef.current;
        if (!rt?.forceBakeDown) return { ms: 0, live: 0, bakedDown: 0 };
        const info = rt.forceBakeDown();
        writeDemoState({
          decalRing: { live: info.live, bakedDown: info.bakedDown },
          bakeDownMs: info.ms,
        });
        return info;
      },
      setBakeDownAuto(enabled: boolean) {
        const rt = runtimeRef.current;
        if (!rt) return;
        rt.bakeDownAuto = enabled;
      },
    };
    return () => {
      delete window.__demolitionProbe;
      delete (window as unknown as { __demolitionRuntime?: DemolitionRuntime }).__demolitionRuntime;
    };
  }, [camera, controlsRef, runtimeRef, sessionRef, setMaterialRef, toolRef, weaponPowerRef]);

  useFrame((_, delta) => {
    if (prewarmed) writeDemoState({ canvasReady: true });
    const runtime = runtimeRef.current;
    if (!runtime) return;
    (window as unknown as { __demolitionRuntime?: DemolitionRuntime }).__demolitionRuntime =
      runtime;

    const session = sessionRef.current;
    if (!session) return;
    session.advance(Math.min(delta, 0.05));

    // Drain once, then fan the canonical batch out to the shared presentation seam.
    dispatchDemolitionPresentation({
      structureKey: 'demolition',
      material: runtime.profile.id,
      wallCenter: runtime.wallCenter,
      events: session.drainEvents()
        .filter((envelope) => envelope.structureKey === session.structureKey)
        .map((envelope) => envelope.event),
      effects: overlayPresentationParticles(fxRef.current, particleRef.current),
      residue: residueRef.current,
      effectsEnabled: effectTier !== 'off',
    });

    const shardLive = fxRef.current?.liveCount() ?? 0;
    setShardParticleCount(runtime, shardLive);

    if (runtime.wallVersion !== lastWallVersion.current) {
      lastWallVersion.current = runtime.wallVersion;
      onWallVersion(runtime.wallVersion);
    }

    const fps = delta > 0 ? 1 / delta : 60;
    statsRef.current.fps = statsRef.current.fps * 0.9 + fps * 0.1;
    statsRef.current.staticCells = runtime.staticCells;
    statsRef.current.dynamicChunks = runtime.dynamicChunks;
    statsRef.current.awake = runtime.awakeCount;

    const snap = getDemolitionSnapshot(runtime);
    writeDemoState({
      loaded: true,
      material: snap.material,
      cellCount: snap.cellCount,
      staticCells: snap.staticCells,
      dynamicChunks: snap.dynamicChunks,
      removedChunks: snap.removedChunks,
      awake: snap.awake,
      fractured: snap.fractured,
      lastImpact: snap.lastImpact,
      collapsedUnsupported: snap.collapsedUnsupported,
      fps: statsRef.current.fps,
      canvasReady: prewarmed || window.__demolitionState?.canvasReady === true,
      maxDynamicY: snap.maxDynamicY,
      fadingChunks: snap.fadingChunks,
      subChunks: snap.subChunks,
      shardParticles: snap.shardParticles,
      minChunkVol: snap.minChunkVol,
      maxChunkVol: snap.maxChunkVol,
      lastPartitionMs: snap.lastPartitionMs,
      lastCarveMs: snap.lastCarveMs,
      tool: snap.tool,
      weaponPower: snap.weaponPower,
      projectileCount: snap.projectileCount,
      charges: snap.charges,
      preCrackedCells: snap.preCrackedCells,
      lastBlast: snap.lastBlast,
      invariantViolations: snap.invariantViolations,
      invariantViolationList: snap.invariantViolationList,
      invariantOverheadMsPerStep: snap.invariantOverheadMsPerStep,
      maxLiveCells: snap.maxLiveCells,
      liveCells: snap.liveCells,
      pristineCellCount: snap.pristineCellCount,
      lastResolve: snap.lastResolve,
      fragmentVolumes: snap.fragmentVolumes,
      clampEngagements: snap.clampEngagements,
      patchEngagements: snap.patchEngagements,
      pileRadius: snap.pileRadius,
      pileHeight: snap.pileHeight,
      simTime: snap.simTime,
      dynCorridorCount: snap.dynCorridorCount,
      dynMeanY: snap.dynMeanY,
      releasedDisplacedCount: snap.releasedDisplacedCount,
      releasedDisplacedFrac: snap.releasedDisplacedFrac,
      embeddedProjectiles: snap.embeddedProjectiles,
      projectilePositions: snap.projectilePositions,
      impactTraces: snap.impactTraces,
      renderCalls: gl.info.render.calls,
      renderTriangles: gl.info.render.triangles,
      renderTextures: gl.info.memory.textures,
      renderGeometries: gl.info.memory.geometries,
      renderPrograms: gl.info.programs?.length ?? 0,
      pixelRatio: gl.getPixelRatio(),
      stepMs: snap.stepMs,
      bodyCountTotal: snap.bodyCountTotal,
      staticBodyCount: snap.staticBodyCount,
      dynamicBodyCount: snap.dynamicBodyCount,
      fadingBodyCount: snap.fadingBodyCount,
      remnantMergeMs: snap.remnantMergeMs,
      remnantMergeCount: snap.remnantMergeCount,
      eventRingCapacity: snap.eventRingCapacity,
      eventRingHistory: snap.eventRingHistory,
      damageLedgerCapacity: snap.damageLedgerCapacity,
      damageLedgerHistory: snap.damageLedgerHistory,
      damageFacts: snap.damageFacts,
      pulverizedVolume: snap.pulverizedVolume,
      sessionPulverizedVolume: snap.sessionPulverizedVolume,
      lastShardVolume: snap.lastShardVolume,
      lastSpawnedChunkVolume: snap.lastSpawnedChunkVolume,
      residueGeneration: snap.residueGeneration,
      eventRingTelemetry: snap.eventRingTelemetry,
      damageLedgerTelemetry: snap.damageLedgerTelemetry,
      decalRing: runtime.decalRing ?? {
        live: runtime.decalStampCount ?? 0,
        bakedDown: runtime.decalBakedDownCount ?? 0,
      },
      bakeDownEvents:
        (runtime.decalBakedDownCount ?? 0) === 0 && (runtime.decalStampCount ?? 0) === 0
          ? []
          : (window.__demolitionState?.bakeDownEvents ?? []),
      ...cameraOrbitSnapshot(camera, controlsRef.current),
    });
  });

  void controlsRef;
  return null;
}

// ---------------------------------------------------------------------------
// Scene root
// ---------------------------------------------------------------------------

function DemoScene({
  runtime,
  sessionRef,
  weaponPowerRef,
  concreteSettings,
  lightingMode,
  effectTier,
  visualState,
  autoOrbit = false,
  runtimeRef,
  statsRef,
  controlsRef,
  setMaterialRef,
  toolRef,
  onFired,
  prewarmed,
  onPrewarmed,
}: {
  runtime: DemolitionRuntime;
  sessionRef: MutableRefObject<DemolitionPageSession | null>;
  weaponPowerRef: MutableRefObject<number>;
  concreteSettings: ConcreteMaterialSettings;
  lightingMode: ConcreteLightingMode;
  effectTier: DemolitionEffectTier;
  visualState: DemolitionVisualState;
  autoOrbit?: boolean;
  runtimeRef: MutableRefObject<DemolitionRuntime | null>;
  statsRef: MutableRefObject<{ fps: number; staticCells: number; dynamicChunks: number; awake: number }>;
  controlsRef: MutableRefObject<OrbitControlsImpl | null>;
  setMaterialRef: MutableRefObject<((id: ProfileId) => Promise<void>) | null>;
  toolRef: MutableRefObject<DemoTool>;
  onFired?: () => void;
  prewarmed: boolean;
  onPrewarmed: () => void;
}) {
  const materials = useDemolitionVisualMaterials(
    runtime.profile.id,
    concreteSettings,
    runtime.wallFull,
    runtime.wallCenter,
  );
  const fxRef = useRef<DemolitionFxController | null>(null);
  const particleRef = useRef<DemolitionParticleController | null>(null);
  const residueRef = useRef<PowderResidueController | null>(null);
  const [wallVersion, setWallVersion] = useState(0);
  const [fractured, setFractured] = useState(false);
  const [residueGeneration, setResidueGeneration] = useState(runtime.residueGeneration);

  useEffect(() => {
    runtimeRef.current = runtime;
    setFractured(runtime.fractured);
    setWallVersion(runtime.wallVersion);
  }, [runtime, runtimeRef]);

  // Material disposal is owned by useProfileMaterials — do not dispose here.

  // Track fracture for intact vs remnant switch.
  useFrame(() => {
    if (runtime.fractured !== fractured) setFractured(runtime.fractured);
    if (runtime.residueGeneration !== residueGeneration) {
      setResidueGeneration(runtime.residueGeneration);
    }
  });

  const orbitTargetY = runtime.wallCenter.y * 0.95;

  return (
    <>
      <DemoStudioEnvironment />
      <SceneLights mode={lightingMode} />
      <Ground />
      <PowderResidueLayer
        material={runtime.profile.id}
        wallHalfZ={runtime.wallHalf.z}
        wallWidth={runtime.wallFull.x}
        residueGeneration={residueGeneration}
        controllerRef={residueRef}
      />
      <ContactShadows
        position={[0, 0.015, 0]}
        opacity={0.34}
        scale={13}
        blur={2.2}
        far={5}
        frames={1}
      />
      <color attach="background" args={['#111821']} />
      <fog attach="fog" args={['#111821', 28, 55]} />

      {materials.ready && !fractured && (
        <IntactWall runtime={runtime} materials={materials.list} atlas={materials.atlas} />
      )}
      {materials.ready && fractured && (
        <FracturedWall
          runtime={runtime}
          materials={materials.list}
          wallVersion={wallVersion}
          atlas={materials.atlas}
        />
      )}
      <Phase1DamageLayer
        runtime={runtime}
        tier={effectTier}
        bakeHandle={materials.handle}
        onBakeDown={(info) => {
          const prev = window.__demolitionState?.bakeDownEvents ?? [];
          writeDemoState({
            decalRing: { live: info.live, bakedDown: info.bakedDown },
            bakeDownMs: info.ms,
            bakeDownEvents: [
              ...prev,
              {
                simTime: runtime.stepper.simTime,
                ms: info.ms,
                live: info.live,
                bakedDown: info.bakedDown,
              },
            ].slice(-12),
          });
          if (info.ms >= 250) {
            demoBakeBegin();
            requestAnimationFrame(() => demoBakeEnd({}));
          }
        }}
      />
      <Cannonballs runtime={runtime} />
      <ChargeMarkers runtime={runtime} />
      {effectTier !== 'off' && (
        <ImpactFx fxRef={fxRef} visualState={visualState} />
      )}
      {effectTier !== 'off' && (
        <DemolitionParticleLayer
          effectTier={effectTier}
          wallCenter={runtime.wallCenter}
          wallHalf={runtime.wallHalf}
          residueGeneration={residueGeneration}
          controllerRef={particleRef}
        />
      )}
      {materials.ready && (
        <FirstHitPrewarm
          liveMaterials={materials.list}
          runtime={runtime}
          residueRef={residueRef}
          fxRef={fxRef}
          onDone={onPrewarmed}
        />
      )}

      <DemoSim
        runtimeRef={runtimeRef}
        sessionRef={sessionRef}
        statsRef={statsRef}
        fxRef={fxRef}
        particleRef={particleRef}
        residueRef={residueRef}
        controlsRef={controlsRef}
        onWallVersion={setWallVersion}
        setMaterialRef={setMaterialRef}
        toolRef={toolRef}
        weaponPowerRef={weaponPowerRef}
        onFired={onFired}
        prewarmed={prewarmed}
        effectTier={effectTier}
      />

      <OrbitControls
        ref={controlsRef}
        makeDefault
        enableDamping
        autoRotate={autoOrbit}
        autoRotateSpeed={0.8}
        dampingFactor={0.08}
        target={[0, orbitTargetY, 0]}
        minDistance={4}
        maxDistance={30}
        maxPolarAngle={Math.PI * 0.49}
      />
      <CameraDampingCutoff controlsRef={controlsRef} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function StatsRows({ hud }: { hud: { fps: number; staticCells: number; dynamicChunks: number; awake: number; fractured: boolean; material: string; bakedActive?: boolean; bakeMs?: number } }) {
  return (
    <>
      <div className="demo-stat-row">
        <span>Material</span>
        <strong data-testid="stat-material">{hud.material}</strong>
      </div>
      {hud.bakedActive ? (
        <div className="demo-stat-row">
          <span>Baked</span>
          <strong data-testid="stat-bake-ms">{(hud.bakeMs ?? 0).toFixed(0)}ms</strong>
        </div>
      ) : null}
      <div className="demo-stat-row">
        <span>Static</span>
        <strong data-testid="stat-static">{hud.staticCells}</strong>
      </div>
      <div className="demo-stat-row">
        <span>Dynamic</span>
        <strong data-testid="stat-dynamic">{hud.dynamicChunks}</strong>
      </div>
      <div className="demo-stat-row">
        <span>Awake</span>
        <strong data-testid="stat-awake">{hud.awake}</strong>
      </div>
      <div className="demo-stat-row">
        <span>FPS</span>
        <strong data-testid="stat-fps">{hud.fps}</strong>
      </div>
      <div className="demo-stat-row">
        <span>State</span>
        <strong>{hud.fractured ? 'fractured' : 'intact'}</strong>
      </div>
    </>
  );
}

export default function Demolition({ onBack }: { onBack: () => void }) {
  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth < MOBILE_BREAKPOINT;
  const effectTier = useMemo(() => resolveDemolitionEffectTier(), []);
  const visualState = useMemo(() => createDemolitionVisualState(effectTier), [effectTier]);
  const [runtime, setRuntime] = useState<DemolitionRuntime | null>(null);
  const [material, setMaterial] = useState<ProfileId>('concrete');
  const [tool, setTool] = useState<DemoTool>('cannon');
  const [weaponPower, setWeaponPowerState] = useState(DEFAULT_WEAPON_POWER);
  const [error, setError] = useState<string | null>(null);
  const [prewarmed, setPrewarmed] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [weaponPickerOpen, setWeaponPickerOpen] = useState(true);
  const [hintVisible, setHintVisible] = useState(true);
  const [concreteLabOpen, setConcreteLabOpen] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('lab') === 'concrete',
  );
  const [concretePreset, setConcretePreset] = useState<ConcretePreset>('hero');
  const [concreteSettings, setConcreteSettings] = useState<ConcreteMaterialSettings>(() => ({
    ...DEFAULT_CONCRETE_SETTINGS,
    seed: 1337,
    source: 'procedural',
    surfaceState: 'hero',
  }));
  const [concreteLighting, setConcreteLighting] = useState<ConcreteLightingMode>('dramatic');
  const [activeConcreteShowcase, setActiveConcreteShowcase] = useState<ConcreteShowcaseId>('hero');
  const [concreteTourActive, setConcreteTourActive] = useState(false);
  const [concreteTourIndex, setConcreteTourIndex] = useState(0);
  const [concreteAutoOrbit, setConcreteAutoOrbit] = useState(false);
  const runtimeRef = useRef<DemolitionRuntime | null>(null);
  const sessionRef = useRef<DemolitionPageSession | null>(null);
  const weaponPowerRef = useRef(DEFAULT_WEAPON_POWER);
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const statsRef = useRef({ fps: 60, staticCells: 0, dynamicChunks: 0, awake: 0 });
  const setMaterialRef = useRef<((id: ProfileId) => Promise<void>) | null>(null);
  const toolRef = useRef<DemoTool>('cannon');
  const concreteActionTimerRef = useRef<number | null>(null);
  const bakeUi = useDemoBakeUi();
  const [hud, setHud] = useState({
    fps: 60,
    staticCells: 0,
    dynamicChunks: 0,
    awake: 0,
    fractured: false,
    material: 'concrete' as string,
    calls: 0,
    triangles: 0,
    textures: 0,
    pixelRatio: 1,
    bakedActive: false,
    bakeMs: 0,
  });

  const dismissHint = useCallback(() => setHintVisible(false), []);

  useEffect(() => {
    window.__demolitionVisualState = visualState;
    return () => {
      delete window.__demolitionVisualState;
    };
  }, [visualState]);

  const applyRuntime = useCallback((rt: DemolitionRuntime) => {
    setPrewarmed(false);
    setRuntime(rt);
    runtimeRef.current = rt;
    setMaterial(rt.profile.id);
    setTool(toolRef.current);
    setWeaponPowerState(weaponPowerRef.current);
    writeDemoState({
      loaded: true,
      material: rt.profile.id,
      tool: toolRef.current,
      weaponPower: weaponPowerRef.current,
      cellCount: rt.baseCellCount,
      staticCells: 0,
      dynamicChunks: 0,
      removedChunks: 0,
      awake: 0,
      fractured: false,
      lastImpact: null,
      collapsedUnsupported: 0,
      fps: 60,
      canvasReady: false,
      bakedActive: false,
      bakeMs: 0,
      bakeFailed: false,
      maxDynamicY: 0,
      fadingChunks: 0,
      subChunks: 0,
      shardParticles: 0,
      minChunkVol: 0,
      maxChunkVol: 0,
    });
  }, []);

  const switchMaterial = useCallback(
    async (id: ProfileId) => {
      if (runtimeRef.current?.profile.id === id && runtime) return;
      setSwitching(true);
      try {
        const session = sessionRef.current ?? await getDemolitionPageSession();
        sessionRef.current = session;
        const rt = await session.replaceProfile(id);
        applyRuntime(rt);
        // Wait one frame so canvasReady + probe rebind settle for E2E.
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
        writeDemoState({
          loaded: true,
          material: rt.profile.id,
          cellCount: rt.baseCellCount,
          canvasReady: false,
        });
      } finally {
        setSwitching(false);
      }
    },
    [applyRuntime, runtime],
  );

  useEffect(() => {
    setMaterialRef.current = switchMaterial;
  }, [switchMaterial]);

  useEffect(() => {
    let cancelled = false;
    getDemolitionPageSession()
      .then((session) => {
        if (cancelled) return;
        sessionRef.current = session;
        weaponPowerRef.current = session.weaponPower;
        toolRef.current = session.tool;
        applyRuntime(session.runtime);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
      // Keep singleton alive across remounts (yard pattern); no dispose here.
    };
  }, [applyRuntime]);

  // HUD poll
  useEffect(() => {
    const id = window.setInterval(() => {
      const s = window.__demolitionState;
      if (!s) return;
      setHud({
        fps: Math.round(s.fps),
        staticCells: s.staticCells,
        dynamicChunks: s.dynamicChunks,
        awake: s.awake,
        fractured: s.fractured,
        material: s.material,
        calls: s.renderCalls ?? 0,
        triangles: s.renderTriangles ?? 0,
        textures: s.renderTextures ?? 0,
        pixelRatio: s.pixelRatio ?? 1,
        bakedActive: s.bakedActive ?? false,
        bakeMs: s.bakeMs ?? 0,
      });
    }, 200);
    return () => window.clearInterval(id);
  }, []);

  // First-visit mobile toast: auto-fade after ~4s (or earlier on fire).
  useEffect(() => {
    if (!isMobile || !hintVisible) return;
    const id = window.setTimeout(() => setHintVisible(false), 4000);
    return () => window.clearTimeout(id);
  }, [isMobile, hintVisible]);

  const handleReset = useCallback(() => {
    sessionRef.current?.reset();
  }, []);

  const selectTool = useCallback((t: DemoTool) => {
    toolRef.current = t;
    setTool(t);
    sessionRef.current?.setTool(t);
    writeDemoState({ tool: t });
    // Deliberately does NOT close the picker: at <=720px the dock flattens the
    // options into always-visible chips and hides the toggle entirely, so
    // unmounting them on selection would strand the user with no way to change
    // weapons again.
  }, []);

  const handleWeaponPower = useCallback((value: number) => {
    const session = sessionRef.current;
    if (!session) return;
    session.setWeaponPower(value);
    weaponPowerRef.current = session.weaponPower;
    setWeaponPowerState(session.weaponPower);
    writeDemoState({ weaponPower: session.weaponPower });
  }, []);

  const applyConcretePreset = useCallback((nextPreset: ConcretePreset) => {
    setConcretePreset(nextPreset);
    setConcreteSettings((current) => ({ ...current, ...CONCRETE_PRESET_PATCHES[nextPreset] }));
  }, []);

  const changeConcreteSettings = useCallback((patch: Partial<ConcreteMaterialSettings>) => {
    setConcreteSettings((current) => ({ ...current, ...patch }));
  }, []);

  const setConcreteSource = useCallback((source: ConcreteSourceMode) => {
    setConcreteSettings((current) => ({ ...current, source }));
    writeDemoState({ concreteSource: source });
  }, []);

  // Keep lab source visible on the E2E bridge whenever settings change.
  useEffect(() => {
    writeDemoState({ concreteSource: concreteSettings.source ?? 'procedural' });
  }, [concreteSettings.source]);

  const setConcreteCamera = useCallback((cameraSpec: ConcreteTourStep['camera']) => {
    const probe = window.__demolitionProbe;
    if (!probe) return;
    const [px, py, pz, tx, ty, tz, fov] = cameraSpec;
    probe.setCamera(px, py, pz, tx, ty, tz, fov);
  }, []);

  const runConcreteStep = useCallback(
    (step: ConcreteTourStep) => {
      setActiveConcreteShowcase(step.showcase);
      applyConcretePreset(step.preset);
      setConcreteLighting(step.lighting);
      setConcreteAutoOrbit(step.autoOrbit ?? false);
      if (step.quality) {
        const quality = step.quality;
        setConcreteSettings((current) => ({ ...current, quality }));
      }
      setConcreteCamera(step.camera);

      if (concreteActionTimerRef.current != null) {
        window.clearTimeout(concreteActionTimerRef.current);
        concreteActionTimerRef.current = null;
      }

      handleReset();
      if (!step.action) return;

      selectTool(step.action === 'blast' ? 'bomb' : 'cannon');
      concreteActionTimerRef.current = window.setTimeout(() => {
        const session = sessionRef.current;
        if (!session) return;
        if (step.action === 'impact') {
          session.impactAt({ x: 0, y: 2.1, z: 0.35 }, 28);
        } else if (step.action === 'blast') {
          session.detonateAt({ x: 0, y: 2.1, z: 0.35 });
        } else {
          const stressPoints: Array<[number, number, number]> = [
            [-1.8, 0.5, 0.35],
            [1.8, 0.5, 0.35],
            [0, 1.05, 0.35],
            [-1.2, 1.8, 0.35],
            [1.2, 1.8, 0.35],
            [0, 2.8, 0.35],
            [0, 3.7, 0.35],
          ];
          for (const [x, y, z] of stressPoints) session.impactAt({ x, y, z }, 32);
        }
        concreteActionTimerRef.current = null;
      }, step.waitMs ?? 550);
    },
    [applyConcretePreset, handleReset, selectTool, setConcreteCamera],
  );

  const startConcreteTour = useCallback(() => {
    setConcreteLabOpen(true);
    if (concreteActionTimerRef.current != null) {
      window.clearTimeout(concreteActionTimerRef.current);
      concreteActionTimerRef.current = null;
    }
    setConcreteTourActive(false);
    setConcreteTourIndex(0);
    window.requestAnimationFrame(() => setConcreteTourActive(true));
  }, []);

  useEffect(() => {
    if (!concreteTourActive) return;
    const step = CONCRETE_TOUR_STEPS[concreteTourIndex];
    if (!step) {
      setConcreteTourActive(false);
      return;
    }
    runConcreteStep(step);
    const duration = step.showcase === 'settled' ? 6500 : step.action ? 5200 : 3600;
    const timer = window.setTimeout(() => {
      if (concreteTourIndex >= CONCRETE_TOUR_STEPS.length - 1) {
        setConcreteTourActive(false);
      } else {
        setConcreteTourIndex((index) => index + 1);
      }
    }, duration);
    return () => window.clearTimeout(timer);
  }, [concreteTourActive, concreteTourIndex, runConcreteStep]);

  useEffect(() => {
    return () => {
      if (concreteActionTimerRef.current != null) {
        window.clearTimeout(concreteActionTimerRef.current);
      }
    };
  }, []);

  const selectConcreteShowcase = useCallback(
    (id: ConcreteShowcaseId) => {
      const step = CONCRETE_TOUR_STEPS.find((candidate) => candidate.showcase === id) ?? CONCRETE_TOUR_STEPS[0];
      runConcreteStep({ ...step, showcase: id });
    },
    [runConcreteStep],
  );

  const materialButtons = (
    <>
      {PROFILE_IDS.map((id) => (
        <button
          key={id}
          type="button"
          className={`demo-material-btn${material === id ? ' is-active' : ''}`}
          data-testid={`material-${id}`}
          aria-pressed={material === id}
          disabled={switching}
          onClick={() => {
            void switchMaterial(id);
          }}
        >
          {DESTRUCTION_PROFILES[id].label}
        </button>
      ))}
    </>
  );

  const toolSegment = (
    <div className="demo-tool-seg" data-testid="tool-selector" role="group" aria-label="Tool">
      {DEMO_WEAPON_OPTIONS.map((option) => (
        <button
          key={option.id}
          type="button"
          className={`demo-tool-btn${tool === option.id ? ' is-active' : ''}`}
          data-testid={option.id === 'cannon' ? 'legacy-tool-cannon' : `tool-${option.id}`}
          aria-pressed={tool === option.id}
          onClick={() => selectTool(option.id)}
          title={`${option.label} - ${option.effect}`}
          aria-label={option.label}
        >
          {option.icon}
          <span className="demo-mobile-tool-label">{option.shortLabel}</span>
        </button>
      ))}
    </div>
  );

  const selectedWeapon = DEMO_WEAPON_OPTIONS.find((option) => option.id === tool) ?? DEMO_WEAPON_OPTIONS[0];
  const weaponPowerControl = (
    <label className="demo-power-control" data-testid="weapon-power-control">
      <span className="demo-power-label">Power <strong>{weaponPower.toFixed(2)}x</strong></span>
      <input
        type="range"
        min={MIN_WEAPON_POWER}
        max={MAX_WEAPON_POWER}
        step="0.05"
        value={weaponPower}
        onChange={(event) => handleWeaponPower(Number(event.currentTarget.value))}
        data-testid="weapon-power"
        aria-label="Weapon power"
        aria-valuetext={`${weaponPower.toFixed(2)} times power`}
      />
    </label>
  );
  const mobileWeaponPicker = (
    <div className="demo-tool-seg demo-tool-seg-mobile" data-testid="mobile-tool-selector" role="group" aria-label="Weapon">
      <button
        type="button"
        className="demo-tool-picker-toggle"
        data-testid="weapon-picker-toggle"
        aria-expanded={weaponPickerOpen}
        aria-label={`Choose weapon. Current: ${selectedWeapon.label}`}
        onClick={() => setWeaponPickerOpen((open) => !open)}
      >
        <span className="demo-tool-picker-icon" aria-hidden="true">{selectedWeapon.icon}</span>
        <span>{selectedWeapon.shortLabel}</span>
        <span className="demo-tool-picker-chevron" aria-hidden="true">{weaponPickerOpen ? '⌄' : '⌃'}</span>
      </button>
      {weaponPickerOpen && (
        <div className="demo-weapon-picker" role="dialog" aria-label="Choose weapon">
          {DEMO_WEAPON_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`demo-weapon-option${tool === option.id ? ' is-active' : ''}`}
              data-testid={`weapon-${option.id}`}
              aria-pressed={tool === option.id}
              onClick={() => selectTool(option.id)}
            >
              <span className="demo-weapon-option-icon" aria-hidden="true">{option.icon}</span>
              <span className="demo-weapon-option-copy">
                <strong>{option.label}</strong>
                <small>{option.effect}</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`demolition-page${isMobile ? ' is-mobile' : ''}`}
      data-testid="demolition-page"
    >
      {isMobile && (
        <header className="mobile-lab-appbar demo-appbar" data-testid="demo-appbar">
          <button type="button" className="demo-exit" onClick={onBack} data-testid="demo-back">
            ← Back
          </button>
          <h1 className="mobile-lab-appbar-title demo-appbar-title">DEMOLITION LAB</h1>
          <button
            type="button"
            className="mobile-lab-appbar-status demo-live-stats"
            data-testid="demo-live-stats"
            aria-expanded={statsOpen}
            aria-label="Toggle full stats"
            onClick={() => setStatsOpen((v) => !v)}
          >
            {hud.fps}fps · {hud.awake} awake
          </button>
        </header>
      )}

      {/* Canvas lives in ONE stable stage across breakpoints — never remount on resize. */}
      <div className="demo-stage" data-testid="demo-stage">
        <Canvas
          className="demo-canvas-root"
          shadows
          camera={{ position: [7.5, 3.8, 8.5], fov: 45, near: 0.1, far: 80 }}
          gl={{
            antialias: true,
            powerPreference: 'high-performance',
            toneMapping: THREE.ACESFilmicToneMapping,
            // Keep a little headroom for the brighter environment highlight.
            toneMappingExposure: 1.04,
            // Opt-in ONLY, via ?capture=1. canvas.captureStream() cannot read a
            // GPU-composited WebGL surface unless the drawing buffer is
            // preserved: without this, a recording is 300+ frames of a repeated
            // still while the sim provably runs. It costs a real per-frame copy,
            // so production must never pay for it -- marketing capture asks for
            // it explicitly.
            preserveDrawingBuffer: CAPTURE_MODE,
          }}
          onCreated={({ gl }) => {
            gl.setClearColor('#0d1016');
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.04;
            gl.domElement.style.touchAction = 'none';
          }}
        >
          {runtime && (
            <DemoScene
              key={runtime.profile.id}
              runtime={runtime}
              concreteSettings={concreteSettings}
              lightingMode={concreteLighting}
              effectTier={effectTier}
              visualState={visualState}
              autoOrbit={concreteAutoOrbit}
              runtimeRef={runtimeRef}
              sessionRef={sessionRef}
              weaponPowerRef={weaponPowerRef}
              statsRef={statsRef}
              controlsRef={controlsRef}
              setMaterialRef={setMaterialRef}
              toolRef={toolRef}
              onFired={dismissHint}
              prewarmed={prewarmed}
              onPrewarmed={() => {
                setPrewarmed(true);
                writeDemoState({ canvasReady: true });
              }}
            />
          )}
        </Canvas>

        {(!runtime || !prewarmed) && !error && !bakeUi.bakeFailed && (
          <div className="demo-loading" data-testid="demo-loading">
            {!runtime
              ? 'Loading physics…'
              : bakeUi.busy || !bakeUi.ready
                ? 'Baking surfaces…'
                : 'Loading physics…'}
          </div>
        )}
        {error && <div className="demo-loading">Physics failed: {error}</div>}
        {switching && <div className="demo-loading">Switching material…</div>}
        {bakeUi.ready && bakeUi.busy && (
          <div className="demo-baking-chip" data-testid="demo-baking-chip">
            <div className="demo-spinner demo-spinner-sm" />
            <span>Baking…</span>
          </div>
        )}
        {bakeUi.bakeFailed && (
          <div className="demo-bake-error" data-testid="demo-bake-error" role="alert">
            Surface bake failed
          </div>
        )}

        {isMobile && hintVisible && (
          <div className="demo-toast" data-testid="demo-toast" role="status">
            tap to fire · drag to orbit
          </div>
        )}

        {isMobile && statsOpen && (
          <div className="demo-stats-overlay" data-testid="demo-stats">
            <StatsRows hud={hud} />
          </div>
        )}

        {!isMobile && (
          <div className="demo-overlay">
            <div className="demo-brand">
              <button type="button" className="demo-exit" onClick={onBack} data-testid="demo-back">
                ← Back
              </button>
              <h1 className="demo-title">Demolition Lab</h1>
              <div className="demo-title-bar" />
            </div>

            <div className="demo-panel" data-testid="demo-stats">
              <StatsRows hud={hud} />
            </div>

            <div className="demo-materials-row">
              {toolSegment}
              {weaponPowerControl}
              <div className="demo-materials" data-testid="material-selector">
                {materialButtons}
              </div>
              <button
                type="button"
                className={`demo-lab-toggle${concreteLabOpen ? ' is-active' : ''}`}
                onClick={() => setConcreteLabOpen((open) => !open)}
                data-testid="concrete-lab-toggle"
                disabled={material !== 'concrete'}
              >
                Concrete Lab
              </button>
            </div>

            <div className="demo-actions">
              <div className="demo-hint">
                {`Left-drag orbit · Right-click throw ${selectedWeapon.label.toLowerCase()}`}
              </div>
              <button
                type="button"
                className="demo-reset"
                onClick={handleReset}
                data-testid="reset-wall"
              >
                Reset wall
              </button>
            </div>
          </div>
        )}

        {concreteLabOpen && material === 'concrete' && (
          <ConcreteMaterialLab
            settings={concreteSettings}
            preset={concretePreset}
            activeShowcase={activeConcreteShowcase}
            tourActive={concreteTourActive}
            tourIndex={concreteTourIndex}
            tourCount={CONCRETE_TOUR_STEPS.length}
            telemetry={{
              fps: hud.fps,
              calls: hud.calls,
              triangles: hud.triangles,
              textures: hud.textures,
              pixelRatio: hud.pixelRatio,
              dynamicChunks: hud.dynamicChunks,
            }}
            onPreset={applyConcretePreset}
            onChange={changeConcreteSettings}
            onQuality={(quality) => changeConcreteSettings({ quality })}
            onSource={setConcreteSource}
            onShowcase={selectConcreteShowcase}
            onStartTour={startConcreteTour}
            onClose={() => {
              setConcreteLabOpen(false);
              setConcreteTourActive(false);
              setConcreteAutoOrbit(false);
            }}
          />
        )}
      </div>

      {isMobile && (
        <div className="mobile-lab-dock demo-dock" data-testid="demo-dock">
          <div className="demo-dock-tools">
            {mobileWeaponPicker}
            {weaponPowerControl}
          </div>
          <div className="demo-dock-utilities">
            <button
              type="button"
              className={`demo-lab-toggle demo-lab-toggle-mobile${concreteLabOpen ? ' is-active' : ''}`}
              onClick={() => setConcreteLabOpen((open) => !open)}
              data-testid="concrete-lab-toggle-mobile"
              aria-expanded={concreteLabOpen}
              aria-label={concreteLabOpen ? 'Close concrete material lab' : 'Open concrete material lab'}
              disabled={material !== 'concrete'}
            >
              {concreteLabOpen ? 'Close' : 'Lab'}
            </button>
            <div className="demo-dock-materials" data-testid="material-selector">
              {materialButtons}
            </div>
            <button
              type="button"
              className="demo-reset demo-reset-icon"
              onClick={handleReset}
              data-testid="reset-wall"
              aria-label="Reset wall"
              title="Reset wall"
            >
              ↻
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
